/* Bookstore Admin — `/bookstore/admin`.
 *
 * Gated by the existing The Loom admin cookie (`require_admin`).
 * Allows admins to manage the entire catalog: genres, books, bundles,
 * marketing copy, and trigger AI cover generation.
 *
 * iter55s — Hook-deps convention (documented after code review):
 *   The many `useCallback(reload, [])` / `useEffect(() => reload(), [reload])`
 *   pairs below intentionally use EMPTY dep arrays. The bodies only
 *   reference:
 *     • Module-level imports (`bookstoreAdminApi`, `adminApi`, `toast`) —
 *       stable across renders.
 *     • React state setters (`setC`, `setRows`, …) — stable by React
 *       guarantee.
 *   No stale-closure risk. External `react-hooks/exhaustive-deps` scans
 *   may still flag these; the project's own ESLint (which does not
 *   enable that rule) considers them clean.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, BookOpen, Loader2, Sparkles, Save, Plus, Trash2, ImageIcon,
  Star, Crown, Megaphone, Users, Mail, Receipt, RotateCw, FileText,
  Eye, EyeOff, CheckCircle, XCircle, Award, ChevronDown, ChevronUp,
  RefreshCw, Send, AlertCircle, DollarSign, History, Filter, X,
} from 'lucide-react';
import { adminApi } from '@/lib/api';
import { bookstoreAdminApi, resolveCoverUrl } from '@/lib/bookstoreApi';
import { cn } from '@/lib/utils';
import BookFileUploadPanel from './BookFileUploadPanel';
import BookCoverPanel      from './BookCoverPanel';
import PodQueuePanel from './PodQueuePanel';
import MarketingThumbnailEditor from './MarketingThumbnailEditor';
import AdminHelpButton from '@/components/admin/AdminHelpDrawer';
import { useCurrency } from '@/contexts/CurrencyContext';
import BookEditor from './admin/BookEditor';
import PriceOverridesEditor from './admin/PriceOverridesEditor';
import EditorGenreTreePanel from '@/pages/editor/EditorGenreTreePanel';
import { startContextualTour } from '@/components/GuidedTour';

const TABS = [
  { id: 'overview',     label: 'Overview' },
  { id: 'books',        label: 'Books' },
  { id: 'genres',       label: 'Genres' },
  { id: 'authors',      label: 'Authors' },
  { id: 'royalties',    label: 'Royalties' },
  { id: 'genre-tree',   label: 'Genre Tree', editorOnly: true },
  { id: 'bundles',      label: 'Bundles' },
  { id: 'pricing',      label: 'Pricing' },
  { id: 'marketing',    label: 'Marketing' },
  { id: 'warnings',     label: 'Warnings' },
  { id: 'synopsis',     label: 'Synopsis Batch' },
  { id: 'all-synopses', label: 'All Synopses'   },
  { id: 'certificates', label: 'Certificates' },
  { id: 'customers',    label: 'Customers' },
  { id: 'orders',       label: 'Orders' },
  { id: 'pod',          label: 'POD Queue' },
  { id: 'thumbnails',   label: 'Thumbnails' },
  { id: 'upcoming',     label: 'Upcoming' },
  { id: 'waitlist',     label: 'Waitlist' },
];

function Login({ onIn }) {
  const [u, setU] = useState('Administrator');
  const [p, setP] = useState('');
  const [busy, setBusy] = useState(false);
  // iter-greyrom — Forgot password? on the bookstore admin sign-in
  // dialog. Reuses the same /admin/password-reset/request endpoint
  // as the main admin console; the emailed link targets
  // /admin/reset-password.
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const sendResetLink = async () => {
    if (forgotBusy) return;
    setForgotBusy(true);
    try {
      const adminUrl = `${window.location.origin}/admin/reset-password`;
      await adminApi.requestPasswordReset(adminUrl);
      setForgotSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send reset link.');
    } finally { setForgotBusy(false); }
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await adminApi.login(p, u); onIn(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Invalid admin login.'); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-24 w-full max-w-sm rounded-xl border border-border bg-card p-7 shadow-panel-active"
          data-testid="bs-admin-login">
      <div className="mb-3 text-[10.5px] uppercase tracking-[0.2em] text-muted-foreground">Company Access</div>
      <div className="mb-4 text-[16px] font-semibold">Bookstore Admin</div>
      <label className="block">
        <span className="mb-1 block text-[11.5px] text-muted-foreground">Admin user</span>
        <select value={u} onChange={(e) => setU(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-[13px] outline-none focus:border-primary"
                data-testid="bs-admin-login-user">
          <option>Administrator</option>
          <option>David</option>
        </select>
      </label>
      <label className="mt-3 block">
        <span className="mb-1 block text-[11.5px] text-muted-foreground">Password</span>
        <input type="password" value={p} onChange={(e) => setP(e.target.value)} required
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-[13px] outline-none focus:border-primary"
                data-testid="bs-admin-login-password" />
      </label>
      <button type="submit" disabled={busy}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-[13px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60"
              data-testid="bs-admin-login-submit">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BookOpen className="h-4 w-4" />}
        Sign in
      </button>
      {forgotSent ? (
        <p className="mt-3 rounded-sm border border-emerald-400/50 bg-emerald-50/60 px-2 py-1.5 text-center text-[11px] text-emerald-900" data-testid="bs-admin-login-reset-sent">
          If a recovery email is configured, a reset link is on its way. It expires in 60 minutes.
        </p>
      ) : (
        <button
          type="button"
          onClick={sendResetLink}
          disabled={forgotBusy}
          data-testid="bs-admin-login-forgot"
          className="mt-3 block w-full text-center text-[11px] text-muted-foreground hover:text-primary hover:underline disabled:opacity-50"
        >
          {forgotBusy ? 'Sending…' : 'Forgot password? Email me a reset link'}
        </button>
      )}
    </form>
  );
}

// ─── Snapshot Sync Panel ─────────────────────────────────────────────────────
// Lets the admin export all bookstore data from preview and import it on
// production so pricing, copy, covers and metadata stay in sync.
function SnapshotSyncPanel() {
  const [publishBusy, setPublishBusy] = useState(false);

  const doPublish = async () => {
    if (!window.confirm(
      'Publish this snapshot to Object Storage?\n\n' +
      'Production will auto-import it on next deploy or restart if the DB is empty or outdated.'
    )) return;
    setPublishBusy(true);
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      const res  = await fetch(`${API}/admin/bookstore/snapshot/publish`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const r = await res.json();
      if (!res.ok) throw new Error(r.detail || 'Publish failed.');
      toast.success(`Published! ${r.total_records} records → Object Storage.`);
    } catch (e) {
      toast.error(String(e.message || 'Publish failed.'));
    } finally { setPublishBusy(false); }
  };
  const [exportBusy, setExportBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = React.useRef(null);

  const doExport = async () => {
    setExportBusy(true);
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      const res  = await fetch(`${API}/admin/bookstore/snapshot`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `bookstore-snapshot-${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Snapshot downloaded — import this file on production.');
    } catch (e) {
      toast.error('Export failed.');
    } finally { setExportBusy(false); }
  };

  const doImport = async (file) => {
    if (!file) return;
    let snapshot;
    try {
      snapshot = JSON.parse(await file.text());
    } catch {
      toast.error('Invalid JSON file — please use an unmodified snapshot export.');
      return;
    }
    const collections = Object.keys(snapshot).filter((k) => k !== '_meta');
    const totalDocs   = collections.reduce((s, k) => s + (snapshot[k]?.length || 0), 0);
    if (!window.confirm(
      `Import snapshot?\n\n${totalDocs} records across ${collections.length} collections will be upserted.\n\nExisting records will be overwritten. This cannot be undone.`
    )) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const r = await bookstoreAdminApi.importSnapshot(snapshot, { overwrite: true });
      setImportResult(r);
      toast.success(`Import complete — ${r.total_upserted} records upserted.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed.');
    } finally {
      setImportBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="mt-4 rounded-md border border-blue-200 bg-blue-50/60 p-4"
         data-testid="bs-admin-snapshot-panel">
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-blue-900">Preview → Production Sync</p>
          <p className="mt-0.5 text-[11.5px] text-blue-700 leading-snug">
            <strong>Publish to production</strong> — uploads all bookstore data to shared Object Storage so the next production deploy auto-imports it.
            <br/>
            <strong>Export / Import</strong> — manual option: download here, upload on another instance.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            {/* Publish to Object Storage (auto-sync on production deploy) */}
            <button
              type="button"
              onClick={doPublish}
              disabled={publishBusy}
              data-testid="bs-admin-snapshot-publish-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-emerald-800 disabled:opacity-60"
            >
              {publishBusy
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RotateCw className="h-3.5 w-3.5" />}
              Publish to production
            </button>

            {/* Export */}
            <button
              type="button"
              onClick={doExport}
              disabled={exportBusy}
              data-testid="bs-admin-snapshot-export-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-700 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-blue-800 disabled:opacity-60"
            >
              {exportBusy
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <FileText className="h-3.5 w-3.5" />}
              Export snapshot (.json)
            </button>

            {/* Import */}
            <label
              className={`inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-blue-800 hover:bg-blue-50 cursor-pointer ${importBusy ? 'opacity-60 pointer-events-none' : ''}`}
              data-testid="bs-admin-snapshot-import-label"
            >
              {importBusy
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RotateCw className="h-3.5 w-3.5" />}
              Import snapshot (.json)
              <input
                ref={fileRef}
                type="file"
                accept=".json,application/json"
                className="sr-only"
                data-testid="bs-admin-snapshot-import-input"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) doImport(f); }}
              />
            </label>
          </div>

          {/* Import result summary */}
          {importResult && (
            <div className="mt-3 rounded-md border border-blue-200 bg-white p-3 text-[11px] text-blue-900"
                 data-testid="bs-admin-snapshot-import-result">
              <p className="font-semibold mb-1">Import result — {importResult.total_upserted} records upserted</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                {Object.entries(importResult.collections || {}).map(([coll, v]) => (
                  <span key={coll} className="text-blue-700">
                    {coll.replace('bookstore_', '')}:&nbsp;
                    <strong>{v.upserted}</strong> upserted
                    {v.skipped > 0 && <span className="text-blue-400">, {v.skipped} skipped</span>}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function Overview() {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const reload = useCallback(async () => setC((await bookstoreAdminApi.overview()).counts), []);
  useEffect(() => { reload(); }, [reload]);
  const seed = async () => { try { const r = await bookstoreAdminApi.seed(); toast.success(r.seeded ? `Seeded ${r.books} books.` : 'Catalog already seeded.'); reload(); } catch (e) { toast.error(e?.response?.data?.detail); } };
  const generate = async (target) => {
    setBusy(true);
    try {
      const r = await bookstoreAdminApi.generateCovers({ target, force: false });
      toast.success(`Generated covers — genres:${r.genres} books:${r.books_front}/${r.books_back}${r.errors.length ? ` errors:${r.errors.length}` : ''}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cover generation failed'); }
    finally { setBusy(false); }
  };
  const downloadCatalog = async () => {
    setDlBusy(true);
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
      const res = await fetch(`${API}/admin/bookstore/catalog.docx`, { credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      const cd   = res.headers.get('Content-Disposition') || '';
      const match = cd.match(/filename="([^"]+)"/);
      a.download = match ? match[1] : 'crown-and-rose-catalog.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Catalog downloaded.');
    } catch (e) {
      toast.error('Could not download catalog.');
    } finally {
      setDlBusy(false);
    }
  };
  return (
    <div data-testid="bs-admin-overview">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-8">
        {c && Object.entries(c).map(([k, v]) => (
          <div key={k} className="rounded-md border border-border bg-card p-3">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">{k.replace('_', ' ')}</div>
            <div className="mt-1 text-2xl font-semibold">{v}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-md border border-border bg-card p-4">
        <div className="text-[13px] font-semibold">Seed & Cover Art</div>
        <p className="mt-1 text-[11.5px] text-muted-foreground">
          Seed the sample catalog (idempotent — only writes when DB is empty),
          then generate AI cover art via Gemini Nano Banana. Each cover takes
          5–15 seconds; the request may run for several minutes.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={seed} data-testid="bs-admin-seed"
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-[12px] font-medium hover:bg-secondary">
            <RotateCw className="h-3.5 w-3.5" /> Seed catalog
          </button>
          <button onClick={() => generate('genres')} disabled={busy} data-testid="bs-admin-gen-genres"
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Generate genre tiles
          </button>
          <button onClick={() => generate('books')} disabled={busy} data-testid="bs-admin-gen-books"
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Generate book covers
          </button>
          <button onClick={downloadCatalog} disabled={dlBusy} data-testid="bs-admin-download-catalog"
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-[12px] font-medium hover:bg-secondary disabled:opacity-60">
            {dlBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Download catalog (.docx)
          </button>
        </div>
      </div>
      <SnapshotSyncPanel />
    </div>
  );
}

// ── Sync Reminder Banner (Feature 6) ───────────────────────────────────────
function SyncReminderBanner({ onGoToSync }) {
  const [syncInfo, setSyncInfo] = useState(null);
  useEffect(() => {
    bookstoreAdminApi.lastSyncTime()
      .then((r) => setSyncInfo(r))
      .catch(() => {});
  }, []);

  if (!syncInfo) return null;
  const lastPublished = syncInfo.last_published_at;
  const daysSince = lastPublished
    ? Math.floor((Date.now() - new Date(lastPublished).getTime()) / 86400000)
    : null;
  const neverPublished = !lastPublished;
  const stale = neverPublished || daysSince > 7;
  if (!stale) return null;

  return (
    <div className="mb-3 flex items-start gap-2.5 rounded-md border border-amber-400/70 bg-amber-50 px-3 py-2.5 text-[12px]"
         data-testid="bs-admin-sync-reminder-banner">
      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
      <div className="flex-1">
        <p className="font-semibold text-amber-800">
          {neverPublished
            ? 'Production snapshot has never been published.'
            : `Last publish was ${daysSince} day${daysSince === 1 ? '' : 's'} ago.`}
        </p>
        <p className="text-amber-700/80 mt-0.5">
          Changes made in Preview (prices, copy, covers) won't appear in production until you publish.
          {' '}
          <button onClick={onGoToSync}
                  className="font-semibold underline hover:text-amber-900"
                  data-testid="bs-admin-sync-reminder-link">
            Publish to Production →
          </button>
        </p>
      </div>
    </div>
  );
}

function Books() {
  const [rows,      setRows]      = useState(null);
  const [genres,    setGenres]    = useState([]);
  const [editing,   setEditing]   = useState(null);
  const [creating,  setCreating]  = useState(false);
  const [fileBook,  setFileBook]  = useState(null);  // iter-40
  const [coverBook, setCoverBook] = useState(null);  // cover image manager
  // Feature 2: Bulk Author Assignment
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkSelected, setBulkSelected]     = useState([]);
  const [bulkAuthorName, setBulkAuthorName] = useState('');
  const [bulkBusy, setBulkBusy]             = useState(false);
  // Feature 4: Front Matter Editor (open book id)
  const [frontMatterBook, setFrontMatterBook] = useState(null);
  // Feature 7: Price History
  const [priceHistoryBook, setPriceHistoryBook] = useState(null);
  const [priceHistory, setPriceHistory]         = useState([]);
  const [phBusy, setPhBusy]                     = useState(false);
  const reload = useCallback(async () => {
    const [booksResp, genresResp] = await Promise.all([
      bookstoreAdminApi.listBooks(),
      bookstoreAdminApi.listGenres(),
    ]);
    setRows(booksResp.books);
    setGenres(genresResp.genres || []);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  // iter55g27 — Book row can gate the spotlight/bestseller/upcoming
  // toggles based on its genre's Visible flag.
  const genreVisible = useCallback((slug) => {
    const g = genres.find((x) => x.slug === slug);
    return g ? (g.enabled !== false) : true;
  }, [genres]);
  const bulkAssign = async () => {
    if (!bulkSelected.length || !bulkAuthorName.trim()) return;
    setBulkBusy(true);
    try {
      const r = await bookstoreAdminApi.bulkAssignAuthor({ book_ids: bulkSelected, author_name: bulkAuthorName.trim() });
      toast.success(`Assigned "${bulkAuthorName}" to ${r.modified} book(s).`);
      setBulkSelected([]);
      setBulkAuthorName('');
      setShowBulkAssign(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk assign failed.');
    } finally { setBulkBusy(false); }
  };

  const openPriceHistory = async (b) => {
    setPriceHistoryBook(b);
    setPhBusy(true);
    try {
      const r = await bookstoreAdminApi.priceHistory(b.id);
      setPriceHistory(r.history || []);
    } catch { setPriceHistory([]); } finally { setPhBusy(false); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"?`)) return;
    await bookstoreAdminApi.deleteBook(b.id); toast.success('Deleted.'); reload();
  };
  const toggle = async (b, field) => {
    // Frontend guard — the backend enforces this too, but blocking here
    // gives an instant explanation instead of a round-trip 400 toast.
    const marketingFlag = field === 'is_spotlight' || field === 'is_bestseller' || field === 'is_upcoming';
    if (marketingFlag && !b[field] && !genreVisible(b.genre_slug)) {
      toast.error(`Set the "${b.genre_slug}" genre to Visible before adding this book to spotlight / best-sellers / up-and-coming.`);
      return;
    }
    try {
      await bookstoreAdminApi.updateBook(b.id, { [field]: !b[field] });
      reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Update failed.');
    }
  };
  return (
    <div data-testid="bs-admin-books">
      {/* Toolbar: New Book + Bulk Author Assignment */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <button
          onClick={() => setShowBulkAssign((v) => !v)}
          data-testid="bs-admin-bulk-assign-toggle"
          className={`inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-[12px] font-medium ${showBulkAssign ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background hover:bg-secondary'}`}>
          <Users className="h-3.5 w-3.5" /> Bulk Author Assign
        </button>
        <button onClick={() => setCreating(true)} data-testid="bs-admin-book-add"
                className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90">
          <Plus className="h-3.5 w-3.5" /> New book
        </button>
      </div>

      {/* Feature 2: Bulk Author Assignment Panel */}
      {showBulkAssign && rows && (
        <div className="mb-4 rounded-md border border-border bg-card p-4 space-y-3" data-testid="bs-admin-bulk-assign-panel">
          <p className="text-[12px] font-semibold">Bulk Assign Author</p>
          <p className="text-[11.5px] text-muted-foreground">Select books below, type the author name, then click Assign.</p>
          <div className="max-h-48 overflow-y-auto rounded border border-border divide-y divide-border">
            {rows.map((b) => (
              <label key={b.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-secondary cursor-pointer text-[11.5px]">
                <input
                  type="checkbox"
                  checked={bulkSelected.includes(b.id)}
                  onChange={(e) => setBulkSelected((s) => e.target.checked ? [...s, b.id] : s.filter((x) => x !== b.id))}
                  className="h-3.5 w-3.5 accent-primary"
                />
                <span className="font-medium">{b.title}</span>
                <span className="text-muted-foreground ml-auto">{b.author || '—'}</span>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              value={bulkAuthorName}
              onChange={(e) => setBulkAuthorName(e.target.value)}
              placeholder="Author name…"
              data-testid="bs-admin-bulk-author-input"
              className="flex-1 rounded border border-border bg-background px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-primary"
            />
            <button
              onClick={bulkAssign}
              disabled={bulkBusy || !bulkSelected.length || !bulkAuthorName.trim()}
              data-testid="bs-admin-bulk-assign-btn"
              className="rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1"
            >
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
              Assign ({bulkSelected.length})
            </button>
          </div>
        </div>
      )}

      {/* Price History Modal (Feature 7) */}
      {priceHistoryBook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bs-admin-price-history-modal">
          <div className="w-full max-w-lg rounded-xl border border-border bg-background p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                <span className="text-[13px] font-semibold">Price History — {priceHistoryBook.title}</span>
              </div>
              <button onClick={() => setPriceHistoryBook(null)} className="rounded p-1 hover:bg-secondary" data-testid="bs-admin-price-history-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            {phBusy && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>}
            {!phBusy && priceHistory.length === 0 && (
              <p className="text-[12px] text-muted-foreground italic">No price changes recorded yet.</p>
            )}
            {!phBusy && priceHistory.length > 0 && (
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="border-b border-border text-[10.5px] uppercase tracking-wider text-muted-foreground text-left">
                    <th className="pb-1.5 pr-3">Date</th>
                    <th className="pb-1.5 pr-3">From</th>
                    <th className="pb-1.5 pr-3">To</th>
                    <th className="pb-1.5">By</th>
                  </tr>
                </thead>
                <tbody>
                  {priceHistory.map((h, i) => (
                    <tr key={i} className="border-b border-border/40">
                      <td className="py-1 pr-3 text-muted-foreground">{(h.changed_at || '').slice(0, 10)}</td>
                      <td className="py-1 pr-3 font-mono">${((h.old_price_cents || 0) / 100).toFixed(2)}</td>
                      <td className="py-1 pr-3 font-mono font-semibold">${((h.new_price_cents || 0) / 100).toFixed(2)}</td>
                      <td className="py-1 text-muted-foreground">{h.changed_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {!rows ? <div className="text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading…</div>
        : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-[12px]">
              <thead className="bg-panel-header/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Genre</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Pages / Words</th>
                  <th className="px-3 py-2 text-center">Flags</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const visible = genreVisible(b.genre_slug);
                  const lockedTitle = visible ? '' : ` — locked: "${b.genre_slug}" genre is hidden. Set genre back to Visible to enable this flag.`;
                  const flagDisabled = !visible;
                  return (
                  <tr key={b.id} className="border-t border-border/60">
                    <td className="px-3 py-2">
                      <div className="font-semibold">{b.title}</div>
                      <div className="text-[10.5px] text-muted-foreground">{b.author}</div>
                      {b.is_visible === false && (
                        <span className="mt-0.5 inline-block rounded-sm bg-muted px-1 py-0.5 text-[9.5px] uppercase tracking-wider text-muted-foreground"
                              title="This book is hidden from the customer bookstore.">
                          hidden
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {b.genre_slug}
                      {!visible && (
                        <span className="ml-1 rounded-sm bg-muted px-1 py-0.5 text-[9.5px] uppercase tracking-wider text-muted-foreground"
                              title="This genre is hidden from the customer bookstore.">
                          hidden
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">${((b.price_cents || 0) / 100).toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-muted-foreground">{b.page_count || 0}p · {(b.word_count || 0).toLocaleString()}w</td>
                    <td className="px-3 py-2 text-center">
                      {(() => {
                        const isVisibleFlag = b.is_visible !== false;
                        return (
                          <button
                            onClick={() => toggle(b, 'is_visible')}
                            title={isVisibleFlag
                              ? 'Visible to customers. Click to hide this book from the storefront.'
                              : 'Hidden from customers. Click to make this book visible.'}
                            data-testid={`bs-book-visible-${b.id}`}
                            className={`mr-1 rounded p-1 ${isVisibleFlag ? 'bg-success text-success-foreground' : 'bg-muted text-muted-foreground'}`}
                          >
                            {isVisibleFlag
                              ? <Eye className="h-3 w-3" />
                              : <EyeOff className="h-3 w-3" />}
                          </button>
                        );
                      })()}
                      <button onClick={() => toggle(b, 'is_spotlight')} title={`Spotlight${lockedTitle}`}
                              data-testid={`bs-book-spotlight-${b.id}`}
                              disabled={flagDisabled && !b.is_spotlight}
                              className={`mr-1 rounded p-1 ${b.is_spotlight ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'} ${(flagDisabled && !b.is_spotlight) ? 'cursor-not-allowed opacity-40' : ''}`}><Sparkles className="h-3 w-3" /></button>
                      <button onClick={() => toggle(b, 'is_bestseller')} title={`Best-seller${lockedTitle}`}
                              data-testid={`bs-book-bestseller-${b.id}`}
                              disabled={flagDisabled && !b.is_bestseller}
                              className={`mr-1 rounded p-1 ${b.is_bestseller ? 'bg-success text-success-foreground' : 'bg-secondary text-muted-foreground'} ${(flagDisabled && !b.is_bestseller) ? 'cursor-not-allowed opacity-40' : ''}`}><Crown className="h-3 w-3" /></button>
                      <button onClick={() => toggle(b, 'is_upcoming')} title={`Up-and-coming${lockedTitle}`}
                              data-testid={`bs-book-upcoming-${b.id}`}
                              disabled={flagDisabled && !b.is_upcoming}
                              className={`mr-1 rounded p-1 ${b.is_upcoming ? 'bg-warning text-warning-foreground' : 'bg-secondary text-muted-foreground'} ${(flagDisabled && !b.is_upcoming) ? 'cursor-not-allowed opacity-40' : ''}`}><Star className="h-3 w-3" /></button>
                      <button onClick={() => toggle(b, 'in_maven_pool')} title="In Maven pool"
                              className={`rounded p-1 ${b.in_maven_pool ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>M</button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {/* Cover thumbnail — click to open BookCoverPanel */}
                      <button
                        onClick={() => setCoverBook(b)}
                        title="Manage cover image"
                        data-testid={`bs-book-cover-${b.id}`}
                        className="mr-1 inline-flex items-center justify-center rounded overflow-hidden border border-border hover:border-primary transition-colors"
                        style={{ width: 22, height: 33 }}
                      >
                        {b.cover_front_url ? (
                          <img
                            src={resolveCoverUrl(b.cover_front_url)}
                            alt="cover"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ImageIcon className="h-3 w-3 text-muted-foreground" />
                        )}
                      </button>
                      <button onClick={() => setFileBook(b)}
                              title="Manuscript file"
                              data-testid={`bs-book-file-${b.id}`}
                              className="rounded px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                        <FileText className="h-3 w-3" />
                      </button>
                      <button onClick={() => setEditing(b)} className="rounded px-2 py-1 text-[11px] hover:bg-secondary">Edit</button>
                      <button onClick={() => setFrontMatterBook(b)}
                              title="Front Matter"
                              data-testid={`bs-book-frontmatter-${b.id}`}
                              className="rounded px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                        <FileText className="h-3 w-3 text-blue-500" />
                      </button>
                      <button onClick={() => openPriceHistory(b)}
                              title="Price History"
                              data-testid={`bs-book-price-history-${b.id}`}
                              className="rounded px-2 py-1 text-[11px] text-muted-foreground hover:bg-secondary hover:text-foreground">
                        <History className="h-3 w-3 text-amber-500" />
                      </button>
                      <button onClick={() => remove(b)} className="rounded px-2 py-1 text-[11px] text-warning hover:bg-warning/10">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      {(editing || creating) && (
        <BookEditor book={editing} create={creating}
                    onClose={() => { setEditing(null); setCreating(false); }}
                    onSaved={() => { setEditing(null); setCreating(false); reload(); }} />
      )}
      {/* Feature 4: Front Matter Editor */}
      {frontMatterBook && (
        <FrontMatterEditor
          book={frontMatterBook}
          onClose={() => setFrontMatterBook(null)}
          onSaved={() => { setFrontMatterBook(null); reload(); }}
        />
      )}
      {fileBook && (
        <BookFileUploadPanel book={fileBook} onClose={() => setFileBook(null)} />
      )}
      {coverBook && (
        <BookCoverPanel
          book={coverBook}
          onClose={() => setCoverBook(null)}
          onUpdated={(updated) => {
            setRows((prev) => prev.map((b) => b.id === updated.id ? { ...b, cover_front_url: updated.cover_front_url } : b));
            setCoverBook(updated);
          }}
        />
      )}
    </div>
  );
}

// ── Front Matter Editor (Feature 4) ──────────────────────────────────────
function FrontMatterEditor({ book, onClose, onSaved }) {
  const [form, setForm] = useState({
    prologue:          book.prologue          || '',
    isbn:              book.isbn              || '',
    copyright_notice:  book.copyright_notice  || '',
    dedications:       book.dedications       || '',
    acknowledgements:  book.acknowledgements  || '',
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await bookstoreAdminApi.updateBook(book.id, form);
      toast.success('Front matter saved.');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const fields = [
    { key: 'prologue',         label: 'Prologue',          rows: 6 },
    { key: 'isbn',             label: 'ISBN',              rows: 1 },
    { key: 'copyright_notice', label: 'Copyright Notice',  rows: 3 },
    { key: 'dedications',      label: 'Dedications',       rows: 3 },
    { key: 'acknowledgements', label: 'Acknowledgements',  rows: 4 },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-12"
         data-testid="bs-admin-front-matter-modal">
      <div className="w-full max-w-2xl rounded-xl border border-border bg-background p-6 shadow-2xl space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[13px] font-semibold">Front Matter Editor</p>
            <p className="text-[11.5px] text-muted-foreground">{book.title}</p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-secondary" data-testid="bs-admin-front-matter-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        {fields.map(({ key, label, rows }) => (
          <div key={key}>
            <label className="block text-[10.5px] uppercase tracking-wider text-muted-foreground mb-0.5">{label}</label>
            {rows === 1
              ? <input
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  data-testid={`bs-front-matter-${key}`}
                  className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-primary"
                />
              : <textarea
                  value={form[key]}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                  rows={rows}
                  data-testid={`bs-front-matter-${key}`}
                  className="w-full rounded border border-border bg-background px-2.5 py-1.5 text-[12px] leading-relaxed resize-y focus:outline-none focus:border-primary"
                />
            }
          </div>
        ))}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded border border-border px-3 py-1.5 text-[12px] hover:bg-secondary">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
                  data-testid="bs-admin-front-matter-save"
                  className="rounded-md bg-primary px-4 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-1">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save Front Matter
          </button>
        </div>
      </div>
    </div>
  );
}

function Genres() {
  const [rows, setRows] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const reload = useCallback(async () => setRows((await bookstoreAdminApi.listGenres()).genres), []);
  useEffect(() => { reload(); }, [reload]);
  const save = async (g, patch) => {
    // iter55g27 — When flipping a genre Visible → Hidden, the backend
    // sweeps its books' spotlight/bestseller/upcoming flags off. Warn
    // the admin so it's never a silent change.
    if (patch.enabled === false && (g.book_count || 0) > 0) {
      if (!window.confirm(
        `Hide "${g.name}"?\n\nAll of its books will be removed from spotlight, best-sellers, and up-and-coming. You can restore the genre later, but you'll need to re-tick the marketing flags manually.`,
      )) return;
    }
    const r = await bookstoreAdminApi.updateGenre(g.id, patch);
    if (r?.cleared_marketing_flags) {
      toast.success(`Saved. Cleared marketing flags on ${r.cleared_marketing_flags} book(s).`);
    } else {
      toast.success('Saved.');
    }
    reload();
  };

  // iter-53o pass 4 — Direct image upload for genre cards.
  const uploadImage = async (g, file) => {
    if (!file) return;
    try {
      await bookstoreAdminApi.uploadGenreImage(g.id, file);
      toast.success(`Uploaded image for "${g.name}".`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    }
  };

  // iter-50b — Bulk-lock convenience: disable every genre with book_count==0.
  const lockEmpty = async () => {
    setBulkBusy(true);
    try {
      const r = await bookstoreAdminApi.lockEmptyGenres();
      if (r.locked_count === 0) toast.info('No empty genres to lock.');
      else toast.success(`Locked ${r.locked_count} empty genre${r.locked_count === 1 ? '' : 's'}: ${r.locked_slugs.join(', ')}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk-lock failed.');
    } finally {
      setBulkBusy(false);
    }
  };

  // iter-romance28 — Bulk-refresh sample chapters for a shelf. Uses
  // the same >400-char skip guard as the standalone script. Runs
  // sequentially against Claude Sonnet 4.5 (10-20s per book).
  const refreshSamples = async (g, force = false) => {
    if (!window.confirm(
      force
        ? `Re-write sample chapters for ALL ${g.book_count || 0} books on "${g.name}"?\n\nEven books that already have real samples will be overwritten.`
        : `Fill missing sample chapters for "${g.name}"?\n\nBooks that already have real samples (>400 chars, non-placeholder) will be skipped. Runs ~10-20s per book.`,
    )) return;
    setBulkBusy(true);
    try {
      const r = await bookstoreAdminApi.refreshShelfSamples(g.slug, force);
      const ok = (r.results || []).filter((x) => x.status === 'ok').length;
      const sk = (r.results || []).filter((x) => x.status === 'skipped').length;
      const er = (r.results || []).filter((x) => x.status === 'error' || x.status === 'empty').length;
      toast.success(`Samples · ${ok} written, ${sk} skipped, ${er} failed.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sample refresh failed.');
    } finally { setBulkBusy(false); }
  };

  if (!rows) return <Loader2 className="h-4 w-4 animate-spin" />;

  const emptyCount = rows.filter((g) => (g.book_count || 0) === 0 && g.enabled !== false).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2">
        <span className="text-[11.5px] text-muted-foreground">
          {rows.length} genres · {emptyCount} live but empty
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(`Re-generate all ${rows.length} genre tile images via Nano Banana?\n\nThis will overwrite every existing tile and may take a minute.`)) return;
              setBulkBusy(true);
              try {
                const r = await bookstoreAdminApi.regenerateAllGenreTiles();
                toast.success(`Genre tiles regenerated: ${r.done ?? 'done'}`);
                reload();
              } catch (e) { toast.error(e?.response?.data?.detail || 'Regeneration failed.'); }
              finally { setBulkBusy(false); }
            }}
            disabled={bulkBusy}
            data-testid="bs-admin-regen-genre-tiles-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-violet-300/70 bg-violet-50 px-2.5 py-1 text-[11.5px] font-semibold text-violet-800 hover:bg-violet-100 disabled:cursor-not-allowed disabled:opacity-50"
            title="Force-regenerate all genre tile images with Nano Banana (overwrites existing)."
          >
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Regenerate all tiles
          </button>
          <button
            type="button"
            onClick={lockEmpty}
            disabled={bulkBusy || emptyCount === 0}
            data-testid="bs-admin-lock-empty-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-300/70 bg-amber-50 px-2.5 py-1 text-[11.5px] font-semibold text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
            title={emptyCount === 0 ? 'No empty genres to lock.' : `Lock ${emptyCount} live-but-empty genre${emptyCount === 1 ? '' : 's'}.`}
          >
            {bulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : '🔒'}
            Lock all empty
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="bs-admin-genres">
      {rows.map((g) => {
        const enabled = g.enabled !== false;
        const noBooks = (g.book_count || 0) === 0;
        const locked  = !enabled || noBooks;
        return (
        <div key={g.id} className="rounded-md border border-border bg-card p-4" data-testid={`bs-admin-genre-card-${g.slug}`}>
          <div className="flex items-center gap-3">
            <label
              className="relative h-12 w-9 shrink-0 cursor-pointer overflow-hidden rounded-sm bg-secondary group"
              title="Click to upload / replace genre image"
              data-testid={`bs-admin-genre-image-${g.slug}`}
            >
              {g.image_url && <img src={resolveCoverUrl(g.image_url)} alt={g.name} className="h-full w-full object-cover" />}
              <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-[9px] font-semibold uppercase tracking-wider text-white opacity-0 transition group-hover:opacity-100">
                Upload
              </span>
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                data-testid={`bs-admin-genre-image-input-${g.slug}`}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(g, f); e.target.value = ''; }}
              />
            </label>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold">{g.name}</div>
              <div className="text-[10.5px] text-muted-foreground">{g.slug} · {g.book_count || 0} book{g.book_count === 1 ? '' : 's'}</div>
            </div>
            {/* iter-50 / iter-53o — Visibility toggle. When admin unchecks Live,
                the genre is HIDDEN from the customer carousel entirely (not
                shown as "Coming soon"). Auto-lock for zero-book genres is
                a separate case handled by the noBooks banner below. */}
            <label className="flex flex-col items-end gap-0.5" title={enabled ? 'Uncheck to hide this genre from the customer bookstore carousel.' : 'Check to make this genre visible in the customer bookstore carousel.'}>
              <span className={`text-[10.5px] uppercase tracking-wider ${enabled ? 'text-primary' : 'text-muted-foreground'}`}>
                {enabled ? 'Visible' : 'Hidden'}
              </span>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => save(g, { enabled: e.target.checked })}
                data-testid={`bs-admin-genre-toggle-${g.slug}`}
                className="h-4 w-4 cursor-pointer accent-primary"
              />
            </label>
          </div>
          {noBooks && enabled && (
            <div
              data-testid={`bs-admin-genre-nobooks-${g.slug}`}
              className="mt-2 rounded-sm border border-amber-300/60 bg-amber-50/40 px-2 py-1.5 text-[10.5px] text-amber-800"
            >
              No books in this genre yet — customers see &ldquo;Coming soon&rdquo; on the tile.
            </div>
          )}
          {!enabled && (
            <div
              data-testid={`bs-admin-genre-hidden-${g.slug}`}
              className="mt-2 rounded-sm border border-rose-300/60 bg-rose-50/40 px-2 py-1.5 text-[10.5px] text-rose-800"
            >
              Hidden — this genre does not appear in the customer carousel.
            </div>
          )}
          <textarea defaultValue={g.blurb} onBlur={(e) => e.target.value !== g.blurb && save(g, { blurb: e.target.value })}
                    rows={3}
                    data-testid={`bs-admin-genre-blurb-${g.slug}`}
                    className="mt-2 w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12px] outline-none focus:border-primary" />
          {/* iter-romance28 — Bulk-refresh sample chapters for the shelf. */}
          {(g.book_count || 0) > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <button type="button" onClick={() => refreshSamples(g, false)}
                      disabled={bulkBusy}
                      data-testid={`bs-admin-genre-refresh-samples-${g.slug}`}
                      title="Ask Claude Sonnet 4.5 to author a ~700-word Chapter 1 opening for every book on this shelf that lacks a real sample."
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary px-2 py-1 text-[10.5px] font-medium hover:bg-secondary/80 disabled:opacity-50">
                {bulkBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                Fill missing samples
              </button>
              <button type="button" onClick={() => refreshSamples(g, true)}
                      disabled={bulkBusy}
                      data-testid={`bs-admin-genre-refresh-samples-force-${g.slug}`}
                      title="Overwrite every sample on this shelf, even books that already have real prose."
                      className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-oxblood disabled:opacity-50">
                Rewrite all
              </button>
            </div>
          )}
          <textarea defaultValue={g.image_prompt} onBlur={(e) => e.target.value !== g.image_prompt && save(g, { image_prompt: e.target.value })}
                    rows={2}
                    placeholder="Image prompt for Nano Banana (used when auto-generating art)"
                    className="mt-2 w-full rounded-md border border-border bg-background px-3 py-1.5 text-[11px] outline-none focus:border-primary" />
          <input
            type="url"
            defaultValue={g.image_url || ''}
            placeholder="Or paste an image URL (e.g. https://…)"
            data-testid={`bs-admin-genre-image-url-${g.slug}`}
            onBlur={(e) => e.target.value !== (g.image_url || '') && save(g, { image_url: e.target.value })}
            className="mt-2 w-full rounded-md border border-border bg-background px-3 py-1.5 text-[11px] outline-none focus:border-primary"
          />
        </div>
        );
      })}
      </div>
    </div>
  );
}

// ─── Authors Panel ────────────────────────────────────────────────────────────
function AuthorsPanel() {
  const [authors, setAuthors] = useState(null);
  const [busyId, setBusyId]   = useState(null);   // portrait regen
  const [editingId, setEditingId] = useState(null); // which card is in edit mode
  const [drafts, setDrafts]   = useState({});       // id → { pen_name, bio, portrait_prompt }
  const [savingEdit, setSavingEdit] = useState(false);
  const [showArchived, setShowArchived] = useState(false); // Feature 5: Filter archived
  const [draftBioId, setDraftBioId] = useState(null);      // Feature 3: Bio approval
  const [bioDrafts, setBioDrafts] = useState({});          // id → draft bio text
  const [bioBusy, setBioBusy] = useState({});              // id → bool

  const reload = useCallback(async () => {
    const r = await bookstoreAdminApi.listAuthors();
    setAuthors(r.authors || []);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const startEdit = (a) => {
    setDrafts((prev) => ({
      ...prev,
      [a.id]: {
        pen_name:        a.pen_name  || '',
        bio:             a.bio       || '',
        portrait_prompt: a.portrait_prompt || '',
      },
    }));
    setEditingId(a.id);
  };
  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (a) => {
    const draft = drafts[a.id] || {};
    setSavingEdit(true);
    try {
      await bookstoreAdminApi.updateAuthor(a.id, {
        pen_name:        draft.pen_name.trim()        || undefined,
        bio:             draft.bio.trim()             || undefined,
        portrait_prompt: draft.portrait_prompt.trim() || undefined,
      });
      toast.success(`${draft.pen_name || a.pen_name} saved.`);
      setEditingId(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSavingEdit(false); }
  };

  const regenPortrait = async (author) => {
    if (!window.confirm(`Re-generate portrait for "${author.pen_name}"?\nThis will overwrite the existing image.`)) return;
    setBusyId(author.id);
    try {
      await bookstoreAdminApi.regenerateAuthorPortrait(author.id);
      toast.success(`Portrait updated for ${author.pen_name}.`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Portrait generation failed.');
    } finally { setBusyId(null); }
  };

  const regenAll = async () => {
    if (!window.confirm(`Re-generate all ${(authors||[]).length} author portraits?\nThis may take several minutes.`)) return;
    setBusyId('__all__');
    try {
      const r = await bookstoreAdminApi.regenerateAllPortraits();
      toast.success(`All portraits regenerated: ${r.done ?? 'done'}`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Portrait generation failed.');
    } finally { setBusyId(null); }
  };

  if (!authors) return <Loader2 className="h-4 w-4 animate-spin" />;

  const anyBusy = busyId !== null;
  // Feature 5: filter out archived authors by default
  const visibleAuthors = showArchived ? authors : authors.filter((a) => a.is_visible !== false);

  // Feature 3: draft a bio for an author
  const draftBio = async (a) => {
    setBioBusy((b) => ({ ...b, [a.id]: true }));
    try {
      const r = await bookstoreAdminApi.draftAuthorBio(a.id);
      setBioDrafts((d) => ({ ...d, [a.id]: r.draft || '' }));
      setDraftBioId(a.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bio draft failed.');
    } finally {
      setBioBusy((b) => ({ ...b, [a.id]: false }));
    }
  };

  const approveBio = async (a) => {
    const text = (bioDrafts[a.id] || '').trim();
    if (!text) { toast.error('Bio text is empty.'); return; }
    setBioBusy((b) => ({ ...b, [a.id]: true }));
    try {
      await bookstoreAdminApi.updateAuthor(a.id, { bio: text });
      toast.success(`Bio published for ${a.pen_name}.`);
      setBioDrafts((d) => { const n = { ...d }; delete n[a.id]; return n; });
      setDraftBioId(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setBioBusy((b) => ({ ...b, [a.id]: false }));
    }
  };

  return (
    <div className="space-y-3" data-testid="bs-admin-authors-panel">
      {/* Toolbar */}
      <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2">
        <span className="text-[11.5px] text-muted-foreground">
          {visibleAuthors.length} author{visibleAuthors.length === 1 ? '' : 's'} · {visibleAuthors.filter((a) => a.portrait_url).length} with portraits
        </span>
        <div className="flex items-center gap-2">
          {/* Feature 5 — Show archived toggle */}
          <label className="flex items-center gap-1.5 cursor-pointer text-[11.5px] text-muted-foreground hover:text-foreground"
                 data-testid="bs-admin-show-archived-toggle">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}
                   className="h-3.5 w-3.5 accent-amber-500" />
            Show archived
          </label>
          <button
            type="button"
            onClick={regenAll}
            disabled={anyBusy}
            data-testid="bs-admin-regen-all-portraits-btn"
            className="inline-flex items-center gap-1.5 rounded-md border border-violet-300/70 bg-violet-50 px-2.5 py-1 text-[11.5px] font-semibold text-violet-800 hover:bg-violet-100 disabled:opacity-50"
          >
            {busyId === '__all__' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Regenerate all portraits
          </button>
        </div>
      </div>

      {/* Author grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="bs-admin-authors-grid">
        {visibleAuthors.map((a) => {
          const isEditing = editingId === a.id;
          const draft     = drafts[a.id] || {};
          const setDraft  = (field, val) =>
            setDrafts((prev) => ({ ...prev, [a.id]: { ...prev[a.id], [field]: val } }));

          return (
            <div key={a.id}
                 className={`rounded-lg border bg-card overflow-hidden flex flex-col transition-opacity ${
                   a.is_visible === false ? 'opacity-50 border-border/40' : 'border-border'
                 }`}
                 data-testid={`bs-admin-author-card-${a.id}`}>

              {/* Portrait */}
              <div className="relative aspect-[3/4] bg-secondary/40 overflow-hidden">
                {a.portrait_url ? (
                  <img
                    src={resolveCoverUrl(a.portrait_url)}
                    alt={a.pen_name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14" viewBox="0 0 24 24" fill="currentColor">
                      <path fillRule="evenodd" d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm-7 8a7 7 0 0 1 14 0H5z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                {busyId === a.id && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  </div>
                )}
                {/* Edit + Visibility toggles — top-right corner of portrait */}
                {!isEditing && (
                  <div className="absolute top-2 right-2 flex gap-1">
                    {/* Visibility toggle */}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const next = a.is_visible === false ? true : false;
                          await bookstoreAdminApi.updateAuthor(a.id, { is_visible: next });
                          toast.success(next ? `${a.pen_name} is now visible.` : `${a.pen_name} archived.`);
                          reload();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Update failed.');
                        }
                      }}
                      disabled={anyBusy || savingEdit}
                      data-testid={`bs-admin-author-visibility-btn-${a.id}`}
                      title={a.is_visible === false ? 'Archived — click to restore visibility' : 'Visible — click to archive'}
                      className={`rounded-full p-1.5 backdrop-blur-sm transition-colors disabled:opacity-40 ${
                        a.is_visible === false
                          ? 'bg-amber-500/80 text-white hover:bg-amber-600/80'
                          : 'bg-black/50 text-white hover:bg-black/70'
                      }`}
                    >
                      {a.is_visible === false
                        ? <EyeOff className="h-3.5 w-3.5" />
                        : <Eye className="h-3.5 w-3.5" />}
                    </button>
                    {/* Edit toggle */}
                    <button
                      type="button"
                      onClick={() => startEdit(a)}
                      disabled={anyBusy || savingEdit}
                      data-testid={`bs-admin-author-edit-btn-${a.id}`}
                      title="Edit bio & pen name"
                      className="rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70 backdrop-blur-sm transition-colors disabled:opacity-40"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* Info / Edit form */}
              <div className="p-3 flex flex-col gap-2 flex-1">
                {isEditing ? (
                  /* ── Edit mode ── */
                  <div className="flex flex-col gap-2" data-testid={`bs-admin-author-edit-form-${a.id}`}>
                    {/* Pen name */}
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Pen name</label>
                      <input
                        type="text"
                        value={draft.pen_name ?? a.pen_name}
                        maxLength={120}
                        data-testid={`bs-admin-author-pen-name-input-${a.id}`}
                        onChange={(e) => setDraft('pen_name', e.target.value)}
                        className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] font-semibold focus:border-primary focus:outline-none"
                      />
                    </div>
                    {/* Bio */}
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
                        Bio <span className="normal-case text-muted-foreground/60">({(draft.bio ?? a.bio ?? '').length}/1200)</span>
                      </label>
                      <textarea
                        value={draft.bio ?? a.bio ?? ''}
                        maxLength={1200}
                        rows={5}
                        data-testid={`bs-admin-author-bio-textarea-${a.id}`}
                        onChange={(e) => setDraft('bio', e.target.value)}
                        className="w-full rounded border border-border bg-background px-2 py-1.5 text-[11.5px] leading-snug text-foreground resize-y focus:border-primary focus:outline-none"
                      />
                      {/* Feature 3: AI Draft Bio */}
                      <button
                        type="button"
                        onClick={() => draftBio(a)}
                        disabled={bioBusy[a.id]}
                        data-testid={`bs-admin-author-draft-bio-btn-${a.id}`}
                        className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-violet-700 hover:text-violet-900 hover:underline disabled:opacity-50"
                      >
                        {bioBusy[a.id] ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                        AI Draft Bio
                      </button>
                      {/* Bio approval panel — only when a draft is ready */}
                      {draftBioId === a.id && bioDrafts[a.id] !== undefined && (
                        <div className="mt-2 rounded border border-violet-300/60 bg-violet-50/70 p-2" data-testid={`bs-admin-author-bio-approval-${a.id}`}>
                          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-violet-700">AI Draft — review &amp; approve</p>
                          <textarea
                            value={bioDrafts[a.id]}
                            rows={5}
                            onChange={(e) => setBioDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                            className="w-full rounded border border-violet-200 bg-white px-2 py-1.5 text-[11.5px] leading-snug text-foreground resize-y focus:border-violet-400 focus:outline-none"
                            data-testid={`bs-admin-author-bio-draft-textarea-${a.id}`}
                          />
                          <div className="flex gap-1.5 mt-1.5">
                            <button
                              type="button"
                              onClick={() => { setDraft('bio', bioDrafts[a.id]); setDraftBioId(null); }}
                              className="flex-1 rounded bg-violet-700 px-2 py-1 text-[10.5px] font-semibold text-white hover:bg-violet-800"
                              data-testid={`bs-admin-author-bio-use-draft-btn-${a.id}`}
                            >
                              Use this draft
                            </button>
                            <button
                              type="button"
                              onClick={() => { setDraftBioId(null); setBioDrafts((d) => { const n = {...d}; delete n[a.id]; return n; }); }}
                              className="rounded border border-violet-300 px-2 py-1 text-[10.5px] text-violet-600 hover:bg-violet-100"
                              data-testid={`bs-admin-author-bio-discard-btn-${a.id}`}
                            >
                              Discard
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Portrait prompt */}
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Portrait prompt (AI)</label>
                      <input
                        type="text"
                        value={draft.portrait_prompt ?? a.portrait_prompt ?? ''}
                        maxLength={600}
                        data-testid={`bs-admin-author-prompt-input-${a.id}`}
                        onChange={(e) => setDraft('portrait_prompt', e.target.value)}
                        placeholder="e.g. dark-haired woman, 30s, dramatic lighting…"
                        className="w-full rounded border border-border bg-background px-2 py-1 text-[11.5px] text-muted-foreground focus:border-primary focus:outline-none"
                      />
                    </div>
                    {/* Save / Cancel */}
                    <div className="flex gap-1.5 pt-1">
                      <button
                        type="button"
                        onClick={() => saveEdit(a)}
                        disabled={savingEdit}
                        data-testid={`bs-admin-author-save-btn-${a.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-1 rounded bg-primary px-2 py-1.5 text-[11.5px] font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
                      >
                        {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        disabled={savingEdit}
                        data-testid={`bs-admin-author-cancel-btn-${a.id}`}
                        className="rounded border border-border px-2 py-1.5 text-[11.5px] text-muted-foreground hover:bg-secondary disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ── Read mode ── */
                  <>
                    <div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-[13px] font-semibold leading-tight">{a.pen_name}</p>
                        {a.is_visible === false && (
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-700"
                                data-testid={`bs-admin-author-archived-badge-${a.id}`}>
                            Archived
                          </span>
                        )}
                      </div>
                      <p className="text-[10.5px] text-muted-foreground font-mono">{a.slug}</p>
                      {(a.book_count != null) && (
                        <p className="text-[10px] text-muted-foreground">{a.book_count} book{a.book_count === 1 ? '' : 's'}</p>
                      )}
                    </div>
                    {a.bio ? (
                      <p className="text-[10.5px] text-muted-foreground leading-snug line-clamp-3">{a.bio}</p>
                    ) : (
                      <p className="text-[10.5px] text-muted-foreground/40 italic">No bio yet — click edit to add one.</p>
                    )}
                    <button
                      type="button"
                      onClick={() => regenPortrait(a)}
                      disabled={anyBusy}
                      data-testid={`bs-admin-regen-portrait-${a.id}`}
                      className="mt-auto inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-[10.5px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50 transition-colors"
                    >
                      <Sparkles className="h-3 w-3" />
                      Regen portrait
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {authors.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-12">No authors found.</p>
      )}
    </div>
  );
}


function Marketing() {
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { bookstoreAdminApi.getMarketing().then((r) => setM(r.marketing || {})); }, []);
  if (!m) return <Loader2 className="h-4 w-4 animate-spin" />;
  const save = async () => {
    setBusy(true);
    try { await bookstoreAdminApi.saveMarketing(m); toast.success('Saved.'); }
    catch (e) { toast.error(e?.response?.data?.detail); }
    finally { setBusy(false); }
  };
  const set = (k, v) => setM({ ...m, [k]: v });
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="bs-admin-marketing">
      {[
        ['hero_headline',  'Hero headline'],
        ['hero_subline',   'Hero subline'],
        ['maven_headline', 'Maven headline'],
        ['maven_subline',  'Maven subline'],
      ].map(([k, l]) => (
        <label key={k} className="block">
          <span className="mb-1 block text-[11px] text-muted-foreground">{l}</span>
          <input value={m[k] || ''} onChange={(e) => set(k, e.target.value)}
                 className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
        </label>
      ))}
      <label className="block">
        <span className="mb-1 block text-[11px] text-muted-foreground">Maven price (USD / month)</span>
        <input type="number" step="0.01" min="0" value={m.maven_price_usd ?? 9.99}
               onChange={(e) => set('maven_price_usd', parseFloat(e.target.value))}
               className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] outline-none focus:border-primary" />
      </label>
      <div className="md:col-span-2 flex justify-end">
        <button onClick={save} disabled={busy} data-testid="bs-admin-marketing-save"
                className="inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-[12.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </button>
      </div>
    </div>
  );
}

function Bundles() {
  const [bundles, setBundles] = useState(null);
  const [books, setBooks] = useState([]);
  // iter-romance25 — Fire the ribbon-anatomy contextual tour on first
  // Bundles-tab open so admins discover the new "Discovery ribbon" +
  // auto-retire expiry inputs without needing to hunt for them.
  // iter-romance25 — Fire the ribbon-anatomy contextual tour on first
  // Bundles-tab open so admins discover the new "Discovery ribbon" +
  // auto-retire expiry inputs without needing to hunt for them.
  // Anchors are per-bundle-row testids, so the first loaded bundle's
  // id is substituted in before firing (a static `runTour` can't
  // resolve a row-scoped anchor).
  useEffect(() => {
    if (!bundles || bundles.length === 0) return;
    const bid = bundles[0].id;
    startContextualTour('admin-bundle-ribbon-open', [
      { id: 'ribbon-input', anchor: `bs-admin-bundle-ribbon-${bid}`,
        title: 'Discovery ribbon',
        body:  'Type a short label (max 40 chars) to add a rotated corner ribbon to the bundle card — great for calling out "New: Title X" after a refresh. Leave blank to hide.' },
      { id: 'ribbon-expiry', anchor: `bs-admin-bundle-ribbon-expiry-${bid}`,
        title: 'Ribbon auto-retire',
        body:  'Set an expiry date/time and the ribbon vanishes on its own — no more forgetting to clear stale badges. Leave blank to keep the ribbon until you manually clear it.' },
    ]);
  }, [bundles]);
  const reload = useCallback(async () => {
    const [bs, bk] = await Promise.all([
      bookstoreAdminApi.listBundles(), bookstoreAdminApi.listBooks(),
    ]);
    setBundles(bs.bundles); setBooks(bk.books);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const titleOf = (id) => books.find((b) => b.id === id)?.title || id;
  const remove = async (b) => {
    if (!window.confirm(`Delete bundle "${b.title}"?`)) return;
    await bookstoreAdminApi.deleteBundle(b.id); reload();
  };
  // iter-romance26 — "New addition" ribbon library. Admin picks from
  // a curated dropdown, or "Custom…" to type free text. All values
  // persist through the same `new_addition_note` field. Empty clears.
  const RIBBON_LIBRARY = [
    { label: '— No ribbon —',       value: '' },
    { label: 'Editor\'s Pick',      value: "Editor's Pick" },
    { label: 'Bestseller',          value: 'Bestseller' },
    { label: 'Just Added',          value: 'Just Added' },
    { label: 'Signed Edition',      value: 'Signed Edition' },
    { label: 'Limited Time',        value: 'Limited Time' },
    { label: 'Reader Favorite',     value: 'Reader Favorite' },
    { label: 'Staff Pick',          value: 'Staff Pick' },
    { label: 'Custom…',             value: '__custom__' },
  ];

  const updatePrice = async (b, price_cents) => { await bookstoreAdminApi.updateBundle(b.id, { bundle_price_cents: price_cents }); reload(); };
  // iter-greyrom followup — "New addition" ribbon copy. Empty string
  // clears the ribbon; identical value skipped to avoid a PUT-storm.
  const updateRibbon = async (b, note) => {
    const prev = b.new_addition_note || '';
    if (prev === note) return;
    await bookstoreAdminApi.updateBundle(b.id, { new_addition_note: note });
    reload();
  };
  // iter-romance25 — Ribbon expiry. Datetime-local input (browser
  // returns YYYY-MM-DDTHH:mm) → ISO 8601 UTC for persistence.
  const updateRibbonExpiry = async (b, localValue) => {
    const iso = localValue ? new Date(localValue).toISOString() : '';
    const prev = b.new_addition_expires_at || '';
    if (prev === iso) return;
    await bookstoreAdminApi.updateBundle(b.id, { new_addition_expires_at: iso });
    reload();
  };
  // iter-53f — Persist on blur (not keystroke) to avoid the per-character
  // PUT/reload storm + out-of-order race that the code review caught.
  const updateOverrides = async (b, po) => {
    const prev = b.price_overrides || {};
    if (JSON.stringify(prev) === JSON.stringify(po)) return;
    await bookstoreAdminApi.updateBundle(b.id, { price_overrides: po });
    reload();
  };
  return (
    <div data-testid="bs-admin-bundles">
      {!bundles ? <Loader2 className="h-4 w-4 animate-spin" />
        : (
          <div className="space-y-3">
            {bundles.map((b) => (
              <div key={b.id} className="rounded-md border border-border bg-card p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[13.5px] font-semibold">{b.title}</div>
                    <div className="text-[11px] text-muted-foreground">{b.blurb}</div>
                    <div className="mt-1 text-[11px] text-muted-foreground">Books: {b.book_ids.map(titleOf).join(' · ')}</div>
                  </div>
                  <div className="text-right">
                    <label className="block">
                      <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Bundle price (¢)</span>
                      <input type="number" defaultValue={b.bundle_price_cents}
                              onBlur={(e) => parseInt(e.target.value, 10) !== b.bundle_price_cents && updatePrice(b, parseInt(e.target.value, 10))}
                              className="ml-2 w-24 rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-primary"
                              data-testid={`bs-admin-bundle-price-${b.id}`} />
                    </label>
                    <div className="mt-1 text-[10.5px] text-muted-foreground">Reg ${(b.regular_price_cents/100).toFixed(2)} · {b.discount_pct}% off</div>
                    <button onClick={() => remove(b)} className="mt-1 text-[10.5px] text-warning hover:underline">Delete</button>
                  </div>
                </div>
                {/* iter-53e — Bundle-level per-currency overrides. */}
                <div className="mt-3" data-testid={`bs-admin-bundle-overrides-${b.id}`}>
                  <PriceOverridesEditor
                    priceCents={b.bundle_price_cents || 0}
                    overrides={b.price_overrides || {}}
                    onChange={(po) => updateOverrides(b, po)}
                    commitMode="blur"
                  />
                </div>
                {/* iter-greyrom followup — Discovery ribbon. */}
                <div className="mt-3">
                  <label className="block">
                    <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                      Discovery ribbon <span className="text-[10px]">(shown as a rotated corner label on the bundle card — pick from the library or "Custom…" to type free text)</span>
                    </span>
                    {/* iter-romance26 — Library dropdown + optional
                        custom-value input for anything not in the list. */}
                    <div className="mt-1 flex items-center gap-2">
                      <select
                        value={RIBBON_LIBRARY.find((r) => r.value === (b.new_addition_note || ''))
                                ? (b.new_addition_note || '')
                                : '__custom__'}
                        onChange={(e) => {
                          if (e.target.value === '__custom__') return;
                          updateRibbon(b, e.target.value);
                        }}
                        data-testid={`bs-admin-bundle-ribbon-library-${b.id}`}
                        className="w-48 rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-primary">
                        {RIBBON_LIBRARY.map((r) => (
                          <option key={r.label} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        defaultValue={b.new_addition_note || ''}
                        onBlur={(e) => updateRibbon(b, e.target.value.trim())}
                        maxLength={40}
                        placeholder="Custom label (up to 40 chars)"
                        className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-primary"
                        data-testid={`bs-admin-bundle-ribbon-${b.id}`}
                      />
                    </div>
                  </label>
                  {/* iter-romance25 — Ribbon expiry (optional). */}
                  <label className="mt-2 block">
                    <span className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                      Ribbon retires on <span className="text-[10px]">(optional; blank = ribbon stays until manually cleared)</span>
                    </span>
                    <input
                      type="datetime-local"
                      defaultValue={b.new_addition_expires_at ? new Date(b.new_addition_expires_at).toISOString().slice(0, 16) : ''}
                      onBlur={(e) => updateRibbonExpiry(b, e.target.value)}
                      className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-primary"
                      data-testid={`bs-admin-bundle-ribbon-expiry-${b.id}`}
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/* iter-53e — Pricing Audit dashboard.
 *
 * Grid view of every book × every supported currency. Each cell shows
 * the price the customer would see today — either an admin-pinned
 * override (highlighted) or the auto-FX conversion (muted). "Ugly FX"
 * prices (anything that doesn't end .99 / .49 / .00) are flagged so
 * the admin can round them with one click into a clean override.
 *
 * Summary header counts: books, total overrides set, books with at
 * least one ugly-FX gap that could be cleaned up.
 */
function PricingAudit() {
  const { supported, convertTo } = useCurrency();
  const [books, setBooks] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const reload = useCallback(async () => {
    const r = await bookstoreAdminApi.listBooks();
    setBooks(r.books || []);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Round a natural-unit price to a "clean" psychological anchor:
  // 2-decimal currencies snap DOWN to the nearest X.99; 0-decimal
  // currencies snap to the nearest round 100 (no .99 concept).
  const cleanRound = (rawMinor, decimals) => {
    if (decimals === 0) return Math.max(0, Math.round(rawMinor / 100) * 100);
    // Snap to the nearest X.99 — always at or just below the FX value
    // so we never accidentally up-price the customer when "cleaning".
    const hundred = Math.floor(rawMinor / 100);
    const snapped = hundred * 100 - 1;
    return Math.max(99, snapped);
  };

  // A minor-unit value is "ugly" if it doesn't sit on a psychological
  // anchor. 2-decimal: must end in .00 / .49 / .99. 0-decimal (JPY):
  // must be a multiple of 100.
  const isUgly = (minor, decimals) => {
    if (decimals === 0) return (minor % 100) !== 0;
    const tail = minor % 100;
    return tail !== 0 && tail !== 49 && tail !== 99;
  };

  const pin = async (book, code, minor) => {
    setSavingId(book.id + ':' + code);
    try {
      const next = { ...(book.price_overrides || {}), [code]: minor };
      await bookstoreAdminApi.updateBook(book.id, { price_overrides: next });
      toast.success(`Pinned ${code} ${minor} for ${book.title}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSavingId(null); }
  };

  const unpin = async (book, code) => {
    setSavingId(book.id + ':' + code);
    try {
      const next = { ...(book.price_overrides || {}) };
      delete next[code];
      await bookstoreAdminApi.updateBook(book.id, { price_overrides: next });
      toast.success(`Cleared ${code} override.`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSavingId(null); }
  };

  if (!books) return <Loader2 className="h-4 w-4 animate-spin" />;

  const codes = supported.filter((c) => c.code !== 'USD');

  // Per-book "needs cleanup" = at least one non-USD currency lacks an
  // override AND its FX value is ugly. Uses the *column* currency, not
  // the active picker currency.
  const cleanupCount = books.filter((b) =>
    codes.some((c) => {
      if (b.price_overrides?.[c.code] != null) return false;
      try { return isUgly(convertTo(b.price_cents || 0, c.code), c.decimals); }
      catch { return false; }
    }),
  ).length;
  const overrideCount = books.reduce(
    (acc, b) => acc + Object.keys(b.price_overrides || {}).length, 0,
  );

  return (
    <div className="space-y-3" data-testid="bs-admin-pricing-audit">
      <div className="flex flex-wrap items-center gap-4 rounded-md border border-border bg-card p-3 text-[12px]">
        <span><strong className="font-semibold">{books.length}</strong> books</span>
        <span className="text-muted-foreground">·</span>
        <span><strong className="font-semibold text-primary">{overrideCount}</strong> pinned override{overrideCount === 1 ? '' : 's'}</span>
        <span className="text-muted-foreground">·</span>
        <span className={cleanupCount > 0 ? 'text-warning' : 'text-muted-foreground'}>
          <strong className="font-semibold">{cleanupCount}</strong> with ugly-FX gaps
        </span>
        {/* Zero-price alert badge */}
        {books.filter((b) => !(b.price_cents > 0)).length > 0 && (
          <>
            <span className="text-muted-foreground">·</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700" data-testid="bs-audit-zero-price-badge">
              {books.filter((b) => !(b.price_cents > 0)).length} unpriced (${'{'}0{'}'})
            </span>
          </>
        )}
        <button onClick={reload}
                data-testid="bs-admin-pricing-refresh"
                className="ml-auto inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] hover:bg-secondary">
          <RotateCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-[12px]">
          <thead className="bg-panel-header/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="sticky left-0 z-10 bg-panel-header/80 px-3 py-2 text-left">Book</th>
              <th className="px-3 py-2 text-right">USD</th>
              {codes.map((c) => (
                <th key={c.code} className="px-3 py-2 text-right font-mono-stat">{c.code}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {books.map((b) => {
              const rowSaving = !!(savingId && savingId.startsWith(b.id + ':'));
              const isUnpriced = !(b.price_cents > 0);
              return (
              <tr key={b.id} className={`border-t border-border ${isUnpriced ? 'bg-red-50/60' : ''}`} data-testid={isUnpriced ? `bs-audit-unpriced-row-${b.id}` : undefined}>
                <td className="sticky left-0 z-10 bg-card/95 px-3 py-2 text-left">
                  <div className="font-medium">{b.title}</div>
                  <div className="text-[10.5px] text-muted-foreground">{b.author}</div>
                  {isUnpriced && (
                    <span className="inline-block rounded-full bg-red-100 px-1.5 py-0.5 text-[9.5px] font-bold text-red-600 mt-0.5" data-testid={`bs-audit-zero-badge-${b.id}`}>
                      UNPRICED
                    </span>
                  )}
                </td>
                {/* USD base price — inline editable */}
                <td className="px-2 py-2 text-right" data-testid={`bs-admin-pricing-cell-${b.id}-USD`}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    defaultValue={((b.price_cents || 0) / 100).toFixed(2)}
                    key={b.price_cents}
                    disabled={rowSaving}
                    data-testid={`bs-admin-pricing-usd-input-${b.id}`}
                    className="w-20 rounded border border-transparent bg-transparent px-1 py-0.5 text-right font-mono-stat text-[12px] text-foreground hover:border-border focus:border-primary focus:bg-card focus:outline-none disabled:opacity-50"
                    onBlur={async (e) => {
                      const val = parseFloat(e.target.value);
                      if (isNaN(val) || val < 0) return;
                      const newCents = Math.round(val * 100);
                      if (newCents === (b.price_cents || 0)) return;
                      setSavingId(b.id + ':USD');
                      try {
                        await bookstoreAdminApi.updateBook(b.id, { price_cents: newCents });
                        toast.success(`${b.title} → $${(newCents / 100).toFixed(2)}`);
                        await reload();
                      } catch (ex) {
                        toast.error(ex?.response?.data?.detail || 'Save failed.');
                      } finally { setSavingId(null); }
                    }}
                  />
                  {savingId === b.id + ':USD' && (
                    <Loader2 className="inline-block h-3 w-3 animate-spin ml-1 text-muted-foreground" />
                  )}
                </td>
                {codes.map((c) => {
                  const override = b.price_overrides?.[c.code];
                  // iter-53f — Convert to *this column's* currency,
                  // not the picker's active currency.
                  const fx = (() => {
                    try { return convertTo(b.price_cents || 0, c.code); }
                    catch { return 0; }
                  })();
                  const decimals = c.decimals;
                  const factor = decimals === 0 ? 1 : 100;
                  const displayMinor = override ?? fx;
                  const ugly = override == null && isUgly(displayMinor, decimals);
                  const saving = savingId === b.id + ':' + c.code;
                  return (
                    <td key={c.code} className="px-2 py-2 text-right" data-testid={`bs-admin-pricing-cell-${b.id}-${c.code}`}>
                      <div className={cn(
                        'font-mono-stat',
                        override != null ? 'font-semibold text-primary' : 'text-muted-foreground',
                        ugly && 'text-warning',
                      )}>
                        {c.symbol}{(displayMinor / factor).toFixed(decimals)}
                      </div>
                      <div className="flex justify-end gap-1 pt-0.5">
                        {override != null ? (
                          <button onClick={() => unpin(b, c.code)} disabled={rowSaving}
                                  data-testid={`bs-admin-pricing-unpin-${b.id}-${c.code}`}
                                  className="text-[9.5px] uppercase tracking-wider text-muted-foreground hover:text-foreground disabled:opacity-50">
                            {saving ? '…' : 'unpin'}
                          </button>
                        ) : (
                          <button onClick={() => pin(b, c.code, cleanRound(fx, decimals))}
                                  disabled={rowSaving}
                                  data-testid={`bs-admin-pricing-pin-${b.id}-${c.code}`}
                                  title={`Pin ${c.symbol}${(cleanRound(fx, decimals) / factor).toFixed(decimals)}`}
                                  className="text-[9.5px] uppercase tracking-wider text-primary hover:underline disabled:opacity-50">
                            {saving ? '…' : (decimals === 0 ? 'pin round' : 'pin .99')}
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[10.5px] text-muted-foreground">
        <strong className="text-foreground">How to read:</strong> bold-primary values are admin-pinned;
        muted values are live FX. Yellow values are &quot;ugly&quot; auto-FX prices that you could pin
        to a clean .99 anchor with one click.
      </div>
    </div>
  );
}



/* iter-romance27 — Content-Tag Taxonomy admin panel.
   Reads the aggregate + telemetry endpoints and lets the admin pick
   which tags are "canonical" (persisted to app_config.canonical_content_tags).
   Canonical tags render with a filled shield icon in the storefront
   chip renderer (planned) so readers see consistent labels across
   shelves. */
function ContentTagsPanel() {
  const [tags, setTags] = useState(null);
  const [canonical, setCanonical] = useState(new Set());
  const [telemetry, setTelemetry] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [aggr, canon, tele] = await Promise.all([
      bookstoreAdminApi.listContentTags(),
      bookstoreAdminApi.getCanonicalTags(),
      bookstoreAdminApi.tagHiddenTelemetry().catch(() => ({ tags: [], total_events: 0 })),
    ]);
    setTags(aggr.tags || []);
    setCanonical(new Set((canon.tags || []).map((t) => t.toLowerCase())));
    setTelemetry(tele);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleCanonical = (t) => {
    setCanonical((prev) => {
      const next = new Set(prev);
      const key = t.toLowerCase();
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const persist = async () => {
    setSaving(true);
    try {
      await bookstoreAdminApi.setCanonicalTags([...canonical]);
      toast.success(`Canonical list saved · ${canonical.size} tags.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (!tags) return <Loader2 className="h-4 w-4 animate-spin" />;

  // Merge telemetry counts by lower-cased tag key for the "hidden by
  // readers" column.
  const teleMap = new Map();
  (telemetry?.tags || []).forEach((row) => {
    teleMap.set(row.tag.toLowerCase(), row);
  });

  return (
    <div data-testid="bs-admin-content-tags">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Reader-warning taxonomy</div>
          <div className="text-[14px] font-semibold tracking-tight">Content tags · {tags.length} distinct</div>
        </div>
        <button onClick={persist} disabled={saving}
                data-testid="bs-admin-canonical-save"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
          Save canonical list ({canonical.size})
        </button>
      </div>
      <div className="mb-3 rounded-md border border-oxblood/30 bg-oxblood/[0.05] px-4 py-3 text-[11.5px] leading-relaxed text-foreground/85">
        <strong>How this works.</strong> Tick a tag to include it in the canonical list. Once saved, canonical tags carry a filled shield in the storefront chip renderer so readers see consistent labels across shelves. Free-typed tags in the Book editor still work — the canonical list is a curation hint, not a hard restriction.
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        <table className="w-full text-[12px]">
          <thead className="bg-panel-header/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Canonical</th>
              <th className="px-3 py-2 text-left">Tag</th>
              <th className="px-3 py-2 text-left">Books</th>
              <th className="px-3 py-2 text-left">Shelves</th>
              <th className="px-3 py-2 text-left">Reader hides</th>
            </tr>
          </thead>
          <tbody>
            {tags.map((row) => {
              const key = row.tag.toLowerCase();
              const on  = canonical.has(key);
              const tele = teleMap.get(key);
              return (
                <tr key={row.tag} className="border-t border-border/60"
                    data-testid={`bs-admin-content-tag-row-${key.replace(/\s+/g, '-')}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggleCanonical(row.tag)}
                      data-testid={`bs-admin-content-tag-canonical-${key.replace(/\s+/g, '-')}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center rounded-full border border-oxblood/40 bg-oxblood/10 px-2 py-0.5 text-[10.5px] font-medium uppercase tracking-wider text-oxblood">
                      {row.tag}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono-stat">{row.count}</td>
                  <td className="px-3 py-2 text-muted-foreground">{(row.shelves || []).join(', ')}</td>
                  <td className="px-3 py-2 font-mono-stat">
                    {tele
                      ? <span title={`${tele.hide} hides · ${tele.show} unhides`}>{tele.net_hidden > 0 ? `+${tele.net_hidden}` : tele.net_hidden}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {telemetry && telemetry.total_events > 0 && (
        <div className="mt-2 text-[10.5px] text-muted-foreground" data-testid="bs-admin-content-tags-tele-note">
          Telemetry · {telemetry.total_events} reader-warning events across all tags. Higher <code>net_hidden</code> = readers most often filter out.
        </div>
      )}
    </div>
  );
}

function ListView({ title, fetcher, columns, testid }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { fetcher().then((d) => setRows(Object.values(d)[0])); }, [fetcher]);
  if (!rows) return <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{title} · {rows.length}</div>
      <div className="mt-2 overflow-x-auto rounded-md border border-border">
        <table className="w-full text-[12px]">
          <thead className="bg-panel-header/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>{columns.map((c) => <th key={c.key} className="px-3 py-2 text-left">{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i} className="border-t border-border/60">
                {columns.map((c) => <td key={c.key} className="px-3 py-2">{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Promote writing-workspace projects to Upcoming bookstore entries ----
const API = process.env.REACT_APP_BACKEND_URL;
function UpcomingPanel() {
  const [projects, setProjects] = useState([]);
  const [genres,   setGenres]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [promoting, setPromoting] = useState(null); // project_id being promoted
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, gRes] = await Promise.all([
        fetch(`${API}/api/admin/bookstore/promotable-projects`, { credentials: 'include' }),
        fetch(`${API}/api/admin/bookstore/genres`,             { credentials: 'include' }),
      ]);
      const pd = await pRes.json();
      const gd = await gRes.json();
      setProjects(pd?.projects || []);
      setGenres((gd?.genres || []).filter(g => g.enabled !== false));
    } catch { toast.error('Failed to load projects.'); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startPromote = (p) => {
    setPromoting(p.project_id);
    setForm({
      genre_slug:   genres[0]?.slug || '',
      price_cents:  999,
      author_name:  p.author_name || '',
      custom_blurb: p.synopsis    || '',
      content_tags: '',
    });
  };

  const submit = async () => {
    if (!promoting) return;
    setBusy(true);
    try {
      const body = {
        project_id:  promoting,
        genre_slug:  form.genre_slug,
        price_cents: parseInt(form.price_cents, 10) || 0,
        author_name: form.author_name || undefined,
        custom_blurb: form.custom_blurb || undefined,
        content_tags: form.content_tags
          ? form.content_tags.split(',').map(t => t.trim()).filter(Boolean)
          : [],
      };
      const r = await fetch(`${API}/api/admin/bookstore/promote-project`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Promotion failed.');
      toast.success(`${d.title} ${d.action === 'created' ? 'added to' : 'updated in'} the bookstore as Upcoming.`);
      setPromoting(null);
      load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <section className="space-y-4 p-4" data-testid="bs-admin-upcoming">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[14px] font-semibold">Promote Projects to Bookstore</h2>
          <p className="text-[11.5px] text-muted-foreground mt-0.5">
            Flag a writing-workspace project as Upcoming so it appears in the landing page carousels.
            Shoppers can read the first 3 chapters as a free "Look Inside" preview.
          </p>
        </div>
        <button onClick={load} className="text-[11px] text-muted-foreground hover:text-foreground transition-colors">
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-[12px] text-muted-foreground">Loading projects…</div>
      ) : projects.length === 0 ? (
        <div className="py-8 text-center text-[12px] text-muted-foreground">No workspace projects found.</div>
      ) : (
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-[12px]">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Title</th>
                <th className="px-3 py-2 text-left font-medium w-32">Words</th>
                <th className="px-3 py-2 text-left font-medium w-36">Updated</th>
                <th className="px-3 py-2 text-left font-medium w-28">Status</th>
                <th className="px-3 py-2 text-right font-medium w-24">Action</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <React.Fragment key={p.project_id}>
                  <tr className={`border-t border-border ${promoting === p.project_id ? 'bg-amber-50/60' : 'hover:bg-muted/30'}`}>
                    <td className="px-3 py-2.5">
                      <p className="font-medium text-foreground truncate max-w-[260px]">{p.title}</p>
                      {p.synopsis && (
                        <p className="text-[11px] text-muted-foreground truncate max-w-[260px] mt-0.5">{p.synopsis}</p>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {p.word_count ? `${(p.word_count / 1000).toFixed(1)}k` : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {p.updated_at ? p.updated_at.slice(0, 10) : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      {p.bookstore_id ? (
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                          {p.is_upcoming ? 'Upcoming' : 'Live'}
                        </span>
                      ) : (
                        <span className="text-[10px] text-muted-foreground">Not promoted</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        onClick={() => promoting === p.project_id ? setPromoting(null) : startPromote(p)}
                        className="text-[11px] font-medium px-2.5 py-1 rounded border border-border hover:bg-primary/10 hover:border-primary/30 transition-colors"
                        data-testid={`promote-project-btn-${p.project_id}`}
                      >
                        {promoting === p.project_id ? 'Cancel' : p.bookstore_id ? 'Edit' : 'Promote'}
                      </button>
                    </td>
                  </tr>
                  {promoting === p.project_id && (
                    <tr className="border-t border-amber-200 bg-amber-50/40">
                      <td colSpan={5} className="px-4 py-4">
                        <div className="grid grid-cols-2 gap-3 max-w-2xl">
                          <div>
                            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Genre *</label>
                            <select
                              value={form.genre_slug}
                              onChange={e => setForm(f => ({...f, genre_slug: e.target.value}))}
                              className="w-full h-8 rounded border border-border bg-background px-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                              data-testid="promote-genre-select"
                            >
                              {genres.map(g => <option key={g.slug} value={g.slug}>{g.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Price (cents) *</label>
                            <input
                              type="number" min={0} value={form.price_cents}
                              onChange={e => setForm(f => ({...f, price_cents: e.target.value}))}
                              className="w-full h-8 rounded border border-border bg-background px-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                              data-testid="promote-price-input"
                            />
                            <p className="text-[10px] text-muted-foreground mt-0.5">e.g. 999 = $9.99 · 0 = Free</p>
                          </div>
                          <div>
                            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Author name</label>
                            <input
                              type="text" value={form.author_name}
                              onChange={e => setForm(f => ({...f, author_name: e.target.value}))}
                              placeholder="Defaults to project author"
                              className="w-full h-8 rounded border border-border bg-background px-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                              data-testid="promote-author-input"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Trigger warnings (comma-separated)</label>
                            <input
                              type="text" value={form.content_tags}
                              onChange={e => setForm(f => ({...f, content_tags: e.target.value}))}
                              placeholder="e.g. violence, dubcon"
                              className="w-full h-8 rounded border border-border bg-background px-2 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                              data-testid="promote-tags-input"
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Custom blurb (leave blank to use project synopsis)</label>
                            <textarea
                              rows={3} value={form.custom_blurb}
                              onChange={e => setForm(f => ({...f, custom_blurb: e.target.value}))}
                              className="w-full rounded border border-border bg-background px-2 py-1.5 text-[12px] focus:outline-none focus:ring-1 focus:ring-primary/50 resize-none"
                              data-testid="promote-blurb-textarea"
                            />
                          </div>
                          <div className="col-span-2 flex items-center gap-2">
                            <button
                              onClick={submit}
                              disabled={busy || !form.genre_slug}
                              className="h-8 px-4 rounded bg-primary text-primary-foreground text-[12px] font-medium disabled:opacity-50 hover:bg-primary/90 transition-colors flex items-center gap-1.5"
                              data-testid="promote-submit-btn"
                            >
                              {busy && <Loader2 className="h-3 w-3 animate-spin" />}
                              {p.bookstore_id ? 'Update Upcoming Entry' : 'Add to Bookstore as Upcoming'}
                            </button>
                            <button
                              onClick={() => setPromoting(null)}
                              className="h-8 px-3 rounded border border-border text-[12px] text-muted-foreground hover:text-foreground transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Shared helpers
// ─────────────────────────────────────────────────────────────────────────────
function SynopsisBookCard({ book, draft, busy, approved, onDraft, onSkip, onApprove, mode }) {
  return (
    <div className={`rounded-lg border bg-card p-4 transition ${approved ? 'border-emerald-300 bg-emerald-50/40 dark:border-emerald-800/40 dark:bg-emerald-900/10' : 'border-border'}`}
         data-testid={`synopsis-book-${book.id}`}>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="font-semibold text-[13px]">{book.title || '—'}</span>
          <span className="ml-2 text-[11.5px] text-muted-foreground">by {book.author || 'Unknown'}</span>
          {book.genre_slug && (
            <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-[10px]">{book.genre_slug}</span>
          )}
          {book.source_project_id && (
            <span className="ml-2 rounded-full bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-[10px] text-blue-700 dark:text-blue-300" title="Linked to a Loom writing project — characters & plot arc available">
              Loom linked
            </span>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          {!approved && !draft && (
            <button onClick={onDraft} disabled={busy}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-[11.5px] hover:bg-secondary disabled:opacity-60">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              {mode === 'rewrite' ? 'Rewrite' : 'Draft'}
            </button>
          )}
          {!approved && (
            <button onClick={onSkip}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1 text-[11.5px] text-muted-foreground hover:bg-secondary">
              <XCircle className="h-3 w-3" /> Skip
            </button>
          )}
          {approved && <span className="inline-flex items-center gap-1 text-[11.5px] text-emerald-600"><CheckCircle className="h-3.5 w-3.5" /> Saved</span>}
        </div>
      </div>

      {/* Original synopsis (rewrite mode only) */}
      {mode === 'rewrite' && book.back_copy && !draft && (
        <p className="mt-1 rounded-md bg-muted/40 px-3 py-2 text-[11.5px] leading-relaxed italic text-muted-foreground line-clamp-3">
          {book.back_copy}
        </p>
      )}

      {/* Draft / rewrite result */}
      {draft && !approved && (
        <div className="mt-3 space-y-2">
          {mode === 'rewrite' && (
            <div className="mb-1 grid grid-cols-2 gap-2 text-[10.5px] text-muted-foreground">
              <div className="rounded bg-muted/40 px-2 py-1"><span className="font-semibold">Original</span><p className="mt-0.5 line-clamp-2 italic">{book.back_copy}</p></div>
              <div className="rounded bg-primary/5 px-2 py-1"><span className="font-semibold text-primary">Rewritten</span></div>
            </div>
          )}
          <textarea
            rows={5}
            value={draft}
            onChange={e => onDraft(null, e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-[12.5px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary"
            data-testid={`synopsis-draft-${book.id}`}
          />
          <button onClick={() => onApprove(draft)} disabled={busy}
                  data-testid={`synopsis-approve-${book.id}`}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-[12px] font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5" />}
            Approve & save
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Synopsis Batch Panel  (Draft missing + Rewrite existing)
// ─────────────────────────────────────────────────────────────────────────────
function SynopsisBatchPanel() {
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const [mode, setMode]         = useState('draft');   // 'draft' | 'rewrite'
  const [books, setBooks]       = useState(null);
  const [drafts, setDrafts]     = useState({});
  const [busy, setBusy]         = useState({});
  const [approved, setApproved] = useState({});
  const [skipped, setSkipped]   = useState({});
  const [genBusy, setGenBusy]   = useState(false);
  const [capCount, setCapCount] = useState(20);  // configurable 1-50 spend cap

  const load = useCallback(async (m = mode) => {
    setBooks(null);
    const url = m === 'draft'
      ? `${API}/admin/bookstore/synopsis-batch/pending`
      : `${API}/admin/bookstore/synopsis-rewrite/eligible`;
    const r = await fetch(url, { credentials: 'include' });
    const d = await r.json();
    setBooks(d.books || []);
    setDrafts({}); setApproved({}); setSkipped({});
  }, [API, mode]);

  useEffect(() => { load(mode); }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateOne = async (bookId, updatedText = null) => {
    // If called from textarea onChange, just update the draft text
    if (updatedText !== undefined && updatedText !== null) {
      setDrafts(v => ({ ...v, [bookId]: updatedText }));
      return;
    }
    setBusy(b => ({ ...b, [bookId]: true }));
    try {
      const endpoint = mode === 'draft'
        ? `${API}/admin/bookstore/synopsis-batch/draft/${bookId}`
        : `${API}/admin/bookstore/synopsis-rewrite/draft/${bookId}`;
      const r = await fetch(endpoint, { method: 'POST', credentials: 'include' });
      const d = await r.json();
      if (!d.ok) throw new Error(d.detail || 'Failed');
      setDrafts(v => ({ ...v, [bookId]: d.draft ?? d.rewritten }));
    } catch (e) { toast.error(`Failed: ${e.message}`); }
    finally { setBusy(b => ({ ...b, [bookId]: false })); }
  };

  const generateAll = async () => {
    const pending = (books || []).filter(b => !drafts[b.id] && !approved[b.id] && !skipped[b.id]);
    if (!pending.length) { toast('Nothing left to generate.'); return; }
    const cap     = Math.min(Math.max(1, capCount), 50);
    const toRun   = pending.slice(0, cap);
    const hitCap  = pending.length > cap;
    setGenBusy(true);
    for (const book of toRun) await generateOne(book.id);
    setGenBusy(false);
    if (hitCap) {
      toast.warning(
        `Cap reached (${cap}/${pending.length}) — review & approve these first, then run again.`,
        { duration: 6000 },
      );
    } else {
      toast.success(`${toRun.length} draft${toRun.length !== 1 ? 's' : ''} ready — review and approve below.`);
    }
  };

  const approve = async (bookId, text) => {
    setBusy(b => ({ ...b, [bookId]: true }));
    try {
      const r = await fetch(`${API}/admin/bookstore/synopsis-batch/approve/${bookId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ back_copy: text }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.detail || 'Save failed');
      setApproved(v => ({ ...v, [bookId]: true }));
      toast.success('Synopsis saved.');
    } catch (e) { toast.error(e.message); }
    finally { setBusy(b => ({ ...b, [bookId]: false })); }
  };

  const visible    = (books || []).filter(b => !skipped[b.id]);
  const pending    = visible.filter(b => !approved[b.id]);
  const doneCount  = Object.values(approved).filter(Boolean).length;
  const isDraft    = mode === 'draft';

  return (
    <div data-testid="bs-admin-synopsis-panel">

      {/* Mode tabs */}
      <div className="mb-4 flex gap-1 rounded-lg border border-border bg-muted/30 p-1 w-fit">
        {[
          { id: 'draft',   label: 'Draft missing',   icon: Sparkles },
          { id: 'rewrite', label: 'Rewrite & refine', icon: RefreshCw },
        ].map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setMode(id)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] font-medium transition ${mode === id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Header */}
      <div className="mb-4 rounded-md border border-border bg-card p-4">
        <div className="text-[13px] font-semibold">
          {isDraft ? 'AI Synopsis — Draft missing blurbs' : 'AI Synopsis — Rewrite & refine existing blurbs'}
        </div>
        <p className="mt-1 text-[11.5px] text-muted-foreground">
          {isDraft
            ? 'Generate first-time back-cover blurbs for books that have none yet.'
            : 'Rewrite existing synopses using Claude — sharpening character depth and plot-arc progression. Books linked to a Loom writing project also pull in character profiles and story arc for richer rewrites.'}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-[11.5px] text-muted-foreground">
            {books === null ? 'Loading…' : `${pending.length} ${isDraft ? 'missing' : 'eligible'} · ${doneCount} approved this session`}
          </span>
          <div className="flex gap-2 ml-auto items-center">
            <button onClick={() => load(mode)} className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-[12px] hover:bg-secondary">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            {/* Spend-cap field — prevents runaway Claude costs on large catalogs */}
            <label className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground" title="Maximum books to draft per run (1–50)">
              Cap:
              <input
                type="number"
                min={1}
                max={50}
                value={capCount}
                onChange={(e) => setCapCount(Math.min(50, Math.max(1, Number(e.target.value) || 1)))}
                disabled={genBusy}
                data-testid="synopsis-cap-input"
                className="w-12 rounded border border-border bg-background px-1.5 py-0.5 text-center text-[12px] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              />
            </label>
            <button onClick={generateAll} disabled={genBusy || !pending.length}
                    data-testid="synopsis-generate-all"
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60">
              {genBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {isDraft ? 'Generate all drafts' : 'Rewrite all'}
            </button>
          </div>
        </div>
      </div>

      {books === null && (
        <div className="flex justify-center py-10 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      )}
      {books !== null && pending.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-[13px] text-muted-foreground">
          {isDraft ? 'All visible books already have a back-cover blurb.' : 'No books with existing synopses found.'}
        </div>
      )}

      <div className="space-y-3">
        {pending.map(book => (
          <SynopsisBookCard
            key={book.id}
            book={book}
            draft={drafts[book.id]}
            busy={!!busy[book.id]}
            approved={!!approved[book.id]}
            mode={mode}
            onDraft={(_, text) => text !== undefined ? setDrafts(v => ({ ...v, [book.id]: text })) : generateOne(book.id)}
            onSkip={() => setSkipped(v => ({ ...v, [book.id]: true }))}
            onApprove={(text) => approve(book.id, text)}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Certificates Panel — record CO cert numbers + email authors
// ─────────────────────────────────────────────────────────────────────────────
function CertificatesPanel() {
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const [items, setItems]   = useState(null);
  const [certNos, setCertNos] = useState({});   // { filingId: string }
  const [notes, setNotes]   = useState({});
  const [busy, setBusy]     = useState({});
  const [done, setDone]     = useState({});

  const load = useCallback(async () => {
    const r = await fetch(`${API}/admin/copyright-filing/list?limit=200`, { credentials: 'include' });
    const d = await r.json();
    // Pre-fill cert numbers for already-registered filings
    const pre = {};
    (d.items || []).forEach(f => { if (f.registration_number) pre[f.filing_id] = f.registration_number; });
    setCertNos(v => ({ ...pre, ...v }));
    setItems(d.items || []);
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const submit = async (filingId) => {
    const reg = (certNos[filingId] || '').trim();
    if (!reg) { toast.error('Enter a certificate number first.'); return; }
    setBusy(b => ({ ...b, [filingId]: true }));
    try {
      const r = await fetch(`${API}/admin/copyright-filing/${filingId}/register`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_number: reg, notes: notes[filingId] || '' }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.detail || 'Failed');
      setDone(v => ({ ...v, [filingId]: true }));
      toast.success(`Certificate recorded & email sent to author.`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(b => ({ ...b, [filingId]: false })); }
  };

  const paid = (items || []).filter(f => f.payment_status === 'paid');
  const backlog = paid.filter(f => !f.registration_number && !done[f.filing_id]);
  const registered = paid.filter(f => f.registration_number || done[f.filing_id]);

  return (
    <div data-testid="bs-admin-certificates-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[13px] font-semibold">Copyright Certificates</div>
          <p className="text-[11.5px] text-muted-foreground">
            {items === null ? 'Loading…' : `${backlog.length} awaiting certificate · ${registered.length} registered`}
          </p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-[12px] hover:bg-secondary">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {items === null && <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>}

      {/* Awaiting registration */}
      {backlog.length > 0 && (
        <div className="mb-6 space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Awaiting Certificate</div>
          {backlog.map(f => (
            <div key={f.filing_id} className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800/40 dark:bg-amber-900/10 p-4"
                 data-testid={`cert-filing-${f.filing_id}`}>
              <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                  <span className="font-semibold text-[13px]">{f.project_title}</span>
                  <span className="ml-2 text-[11.5px] text-muted-foreground">by {f.author_name}</span>
                  <div className="mt-0.5 flex flex-wrap gap-1.5 text-[10.5px] text-muted-foreground">
                    <span>Ref: {f.reference_number}</span>
                    <span>·</span>
                    <span>{f.filing_type}</span>
                    <span>·</span>
                    <span>Paid {f.paid_at?.slice(0, 10)}</span>
                    <span>·</span>
                    <span>{f.author_email}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  placeholder="CO Registration number (e.g. TX-9-123-456)"
                  value={certNos[f.filing_id] || ''}
                  onChange={e => setCertNos(v => ({ ...v, [f.filing_id]: e.target.value }))}
                  className="flex-1 min-w-[220px] rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] focus:outline-none focus:ring-1 focus:ring-primary"
                  data-testid={`cert-input-${f.filing_id}`}
                />
                <input
                  type="text"
                  placeholder="Admin notes (optional)"
                  value={notes[f.filing_id] || ''}
                  onChange={e => setNotes(v => ({ ...v, [f.filing_id]: e.target.value }))}
                  className="flex-1 min-w-[180px] rounded-md border border-border bg-background px-3 py-1.5 text-[12.5px] focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button onClick={() => submit(f.filing_id)} disabled={busy[f.filing_id]}
                        data-testid={`cert-submit-${f.filing_id}`}
                        className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-60">
                  {busy[f.filing_id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Record & email author
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Already registered */}
      {registered.length > 0 && (
        <div>
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Registered</div>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-[12px]">
              <thead className="border-b border-border bg-muted/30">
                <tr>
                  {['Title', 'Author', 'Cert No.', 'Type', 'Registered', 'Email sent'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {registered.map(f => (
                  <tr key={f.filing_id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-3 py-2 font-medium">{f.project_title}</td>
                    <td className="px-3 py-2 text-muted-foreground">{f.author_name}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{f.registration_number || certNos[f.filing_id] || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground">{f.filing_type}</td>
                    <td className="px-3 py-2 text-muted-foreground">{(f.registered_at || '').slice(0, 10) || '—'}</td>
                    <td className="px-3 py-2">
                      {f.certificate_email_sent || done[f.filing_id]
                        ? <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
                        : <XCircle    className="h-3.5 w-3.5 text-muted-foreground" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {items !== null && paid.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-8 text-center text-[13px] text-muted-foreground">
          No paid copyright filings yet.
        </div>
      )}
    </div>
  );
}

/* ── All Synopses panel ──────────────────────────────────────────────────── */
function AllSynopsesPanel() {
  const API = process.env.REACT_APP_BACKEND_URL + '/api';
  const [books, setBooks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [query, setQuery] = React.useState('');
  const [filter, setFilter] = React.useState('approved'); // 'all' | 'approved' | 'pending'
  const [copied, setCopied] = React.useState(null);

  React.useEffect(() => {
    setLoading(true);
    fetch(`${API}/admin/bookstore/synopsis-batch/all`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setBooks([...(d.approved || []), ...(d.pending || [])]))
      .catch(() => toast.error('Failed to load synopses.'))
      .finally(() => setLoading(false));
  }, [API]);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const q = query.trim().toLowerCase();
  const visible = books.filter((b) => {
    if (filter === 'approved' && !(b.back_copy || '').trim()) return false;
    if (filter === 'pending'  &&  (b.back_copy || '').trim()) return false;
    if (!q) return true;
    return (b.title || '').toLowerCase().includes(q) ||
           (b.author || '').toLowerCase().includes(q) ||
           (b.back_copy || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="all-synopses-panel">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search by title, author or text…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-8 flex-1 min-w-[200px] rounded border border-border bg-background px-3 text-[12.5px] outline-none focus:ring-1 focus:ring-primary"
          data-testid="synopsis-search"
        />
        {['all', 'approved', 'pending'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`rounded px-3 py-1 text-[11.5px] font-medium capitalize transition-colors ${
              filter === f
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-muted-foreground hover:text-foreground'
            }`}
          >
            {f}
          </button>
        ))}
        <a
          href={`${API}/admin/bookstore/synopsis-batch/export.docx`}
          className="ml-auto inline-flex items-center gap-1.5 rounded bg-primary/10 px-3 py-1 text-[11.5px] font-medium text-primary hover:bg-primary/20 transition-colors"
          data-testid="synopsis-download-docx"
        >
          ↓ Download DOCX
        </a>
      </div>

      {/* Stats row */}
      <p className="text-[11px] text-muted-foreground">
        Showing {visible.length} of {books.length} books
        &nbsp;·&nbsp;
        {books.filter((b) => (b.back_copy || '').trim()).length} approved,&nbsp;
        {books.filter((b) => !(b.back_copy || '').trim()).length} pending
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground py-6">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          Loading synopses…
        </div>
      )}

      {/* Cards */}
      <div className="space-y-3">
        {visible.map((b) => {
          const hasCopy = !!(b.back_copy || '').trim();
          return (
            <div
              key={b.id}
              className={`rounded-lg border p-4 transition-colors ${
                hasCopy
                  ? 'border-border bg-card'
                  : 'border-dashed border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/20'
              }`}
              data-testid={`synopsis-card-${b.id}`}
            >
              {/* Header row */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-[14px] font-semibold leading-snug">
                    {b.title || <span className="text-muted-foreground italic">Untitled</span>}
                  </h3>
                  <p className="mt-0.5 text-[11.5px] text-muted-foreground">
                    {[b.author, b.genre_slug].filter(Boolean).join(' · ')}
                  </p>
                </div>
                {hasCopy ? (
                  <button
                    onClick={() => copy(b.back_copy, b.id)}
                    className="shrink-0 rounded px-2 py-0.5 text-[11px] font-medium bg-secondary hover:bg-secondary/80 transition-colors"
                    title="Copy synopsis to clipboard"
                    data-testid={`synopsis-copy-${b.id}`}
                  >
                    {copied === b.id ? '✓ Copied' : 'Copy'}
                  </button>
                ) : (
                  <span className="shrink-0 rounded bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    Pending
                  </span>
                )}
              </div>

              {/* Synopsis body */}
              {hasCopy ? (
                <p className="mt-3 text-[13px] leading-relaxed whitespace-pre-wrap text-foreground/80">
                  {b.back_copy}
                </p>
              ) : (
                <p className="mt-2 text-[12px] italic text-muted-foreground">
                  No synopsis yet — go to Synopsis Batch to generate one.
                </p>
              )}
            </div>
          );
        })}

        {!loading && visible.length === 0 && (
          <p className="py-8 text-center text-[13px] text-muted-foreground">
            No books match your search.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Inline SVG Sparkline (no lib needed) ───────────────────────────────────
function Sparkline({ values = [], width = 64, height = 20, color = '#22c55e' }) {
  const safe = values.map((v) => Math.max(0, v || 0));
  const max  = Math.max(1, ...safe);
  if (safe.every((v) => v === 0)) {
    return <span className="text-[9.5px] text-muted-foreground/50 italic">no data</span>;
  }
  const pts = safe.map((v, i) => {
    const x = (i / Math.max(1, safe.length - 1)) * width;
    const y = height - (v / max) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="overflow-visible" style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      {safe.map((v, i) => {
        const x = (i / Math.max(1, safe.length - 1)) * width;
        const y = height - (v / max) * height;
        return v > 0 ? <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r="1.5" fill={color} /> : null;
      })}
    </svg>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//  Royalties Dashboard  (Author Payout Panel)
// ───────────────────────────────────────────────────────────────────────────
function RoyaltiesDashboard() {
  const [data, setData]         = useState(null);
  const [busy, setBusy]         = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [search, setSearch]     = useState('');

  const load = async () => {
    setBusy(true);
    try { setData(await bookstoreAdminApi.authorRoyalties()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load royalties.'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markPaid = async (author) => {
    if (!window.confirm(`Mark all pending royalties as paid for ${author.pen_name}?`)) return;
    setPayingId(author.author_user_id);
    try {
      const r = await bookstoreAdminApi.markRoyaltiesPaid({
        author_user_id:  author.author_user_id,
        note:            `Bookstore payout — ${author.pen_name}`,
        idempotency_key: `bs-payout-${author.author_user_id}-${Date.now()}`,
      });
      toast.success(`Royalties marked as paid for ${author.pen_name}.`);
      // Offer instant receipt download
      if (r?.payout_id) {
        const url = bookstoreAdminApi.payoutReceiptUrl(r.payout_id);
        window.open(url, '_blank');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Payout failed.');
    } finally { setPayingId(null); }
  };

  const fmt = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const authors = (data?.authors || []).filter((a) =>
    !search || (a.pen_name + a.email).toLowerCase().includes(search.toLowerCase())
  );
  const totalPending  = authors.reduce((s, a) => s + (a.pending_cents  || 0), 0);
  const totalLifetime = authors.reduce((s, a) => s + (a.lifetime_cents || 0), 0);

  return (
    <div className="space-y-4" data-testid="bs-royalties-dashboard">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Total Authors',       value: (data?.authors || []).length,                              testid: 'bs-roy-total-authors' },
          { label: 'Lifetime Royalties',  value: fmt(totalLifetime),                                        testid: 'bs-roy-lifetime' },
          { label: 'Pending Payouts',     value: fmt(totalPending),   highlight: totalPending > 0,          testid: 'bs-roy-pending' },
          { label: 'Authors with Pending',value: authors.filter((a) => a.pending_cents > 0).length,        testid: 'bs-roy-authors-pending' },
        ].map(({ label, value, highlight, testid }) => (
          <div key={label} className={`rounded-md border px-3 py-2 ${highlight ? 'border-amber-400/70 bg-amber-50' : 'border-border bg-card'}`} data-testid={testid}>
            <p className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className={`text-[18px] font-bold font-mono ${highlight ? 'text-amber-800' : 'text-foreground'}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filter by pen name or email…"
               className="flex-1 rounded border border-border bg-background px-3 py-1.5 text-[12px] focus:outline-none focus:border-primary"
               data-testid="bs-roy-search" />
        <button onClick={load} disabled={busy}
                className="inline-flex items-center gap-1 rounded border border-border px-2.5 py-1.5 text-[12px] hover:bg-secondary disabled:opacity-50"
                data-testid="bs-roy-refresh">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-[12px]" data-testid="bs-roy-table">
          <thead>
            <tr className="border-b border-border bg-secondary/30 text-[10.5px] uppercase tracking-wider text-muted-foreground text-left">
              <th className="px-3 py-2">Author</th>
              <th className="px-3 py-2">12-wk Trend</th>
              <th className="px-3 py-2 text-right">Lifetime</th>
              <th className="px-3 py-2 text-right">Paid Out</th>
              <th className="px-3 py-2 text-right">Pending</th>
              <th className="px-3 py-2 text-right">Sales</th>
              <th className="px-3 py-2 text-right">Last Sale</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {!data && (
              <tr><td colSpan={7} className="px-3 py-6 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-muted-foreground" /></td></tr>
            )}
            {data && authors.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground italic">No author royalty records yet.</td></tr>
            )}
            {authors.map((a) => (
              <tr key={a.author_user_id} className="border-b border-border/40 hover:bg-secondary/20" data-testid={`bs-roy-row-${a.author_user_id}`}>
                <td className="px-3 py-2">
                  <p className="font-semibold">{a.pen_name}</p>
                  <p className="text-[10.5px] text-muted-foreground">{a.email || a.author_user_id?.slice(0,12)}</p>
                </td>
                <td className="px-3 py-2" data-testid={`bs-roy-spark-${a.author_user_id}`}>
                  <Sparkline values={a.weekly_spark || []} color={a.pending_cents > 0 ? '#f59e0b' : '#22c55e'} />
                </td>
                <td className="px-3 py-2 text-right font-mono">{fmt(a.lifetime_cents)}</td>
                <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmt(a.paid_cents)}</td>
                <td className={`px-3 py-2 text-right font-mono font-semibold ${a.pending_cents > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                  {fmt(a.pending_cents)}
                </td>
                <td className="px-3 py-2 text-right">{a.lifetime_sales}</td>
                <td className="px-3 py-2 text-right text-muted-foreground">{(a.last_sold_at || '—').slice(0, 10)}</td>
                <td className="px-3 py-2 text-right">
                  {a.pending_cents > 0 ? (
                    <button onClick={() => markPaid(a)} disabled={payingId === a.author_user_id}
                            data-testid={`bs-roy-mark-paid-${a.author_user_id}`}
                            className="inline-flex items-center gap-1 rounded-md border border-emerald-400/70 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
                      {payingId === a.author_user_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <DollarSign className="h-3 w-3" />}
                      Mark Paid
                    </button>
                  ) : (
                    <span className="text-[10.5px] text-muted-foreground italic">Settled</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



export default function BookstoreAdmin() {
  const [tab, setTab] = useState('overview');
  const [ok, setOk] = useState(null);
  const [scopes, setScopes] = useState(null);
  const verify = useCallback(async () => {
    try { await adminApi.verify(); setOk(true); } catch { setOk(false); }
  }, []);
  useEffect(() => { verify(); }, [verify]);
  // iter-51z — load the admin's scopes so we can hide the The Loom admin
  // cross-link when the account doesn't have that scope.
  useEffect(() => {
    if (!ok) return;
    adminApi.me().then(r => setScopes(r?.scopes || [])).catch(() => setScopes([]));
  }, [ok]);
  if (ok === null) return <div className="flex h-screen items-center justify-center text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Checking access…</div>;
  if (!ok) return <Login onIn={() => setOk(true)} />;
  const showProWriterLink = scopes === null || scopes.includes('pro_writer');
  const canEditGenreTree = scopes === null || scopes.includes('pro_writer') || scopes.includes('editor');
  const visibleTabs = TABS.filter((row) => !row.editorOnly || canEditGenreTree);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="bs-admin-page">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Link to="/bookstore" className="inline-flex items-center gap-1 rounded-sm px-2.5 py-1 text-[12px] text-muted-foreground hover:bg-secondary hover:text-foreground">
              <ArrowLeft className="h-3 w-3" /> Storefront
            </Link>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">Company Access · Bookstore</div>
          </div>
          <div className="flex items-center gap-1">
            <AdminHelpButton env="bookstore" currentPanelId={tab} />
            {showProWriterLink && (
              <Link to="/admin" className="rounded-sm bg-secondary px-2.5 py-1 text-[12px] hover:bg-secondary/80" data-testid="bs-admin-prowriter-link">The Loom Admin →</Link>
            )}
          </div>
        </div>
      </header>
      <div className="border-b border-border bg-card/40">
        <div className="mx-auto flex max-w-6xl gap-1 px-6 py-2 text-[12px]">
          {visibleTabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
                    data-testid={`bs-admin-tab-${t.id}`}
                    className={`rounded-sm px-3 py-1 ${tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <main className="mx-auto max-w-6xl px-6 py-6">
        <SyncReminderBanner onGoToSync={() => setTab('overview')} />
        {tab === 'overview'      && <Overview />}
        {tab === 'books'         && <Books />}
        {tab === 'genres'        && <Genres />}
        {tab === 'authors'       && <AuthorsPanel />}
        {tab === 'genre-tree'    && <EditorGenreTreePanel />}
        {tab === 'bundles'       && <Bundles />}
        {tab === 'pricing'       && <PricingAudit />}
        {tab === 'marketing'     && <Marketing />}
        {tab === 'warnings'      && <ContentTagsPanel />}
        {tab === 'synopsis'      && <SynopsisBatchPanel />}
        {tab === 'all-synopses'  && <AllSynopsesPanel />}
        {tab === 'royalties'     && <RoyaltiesDashboard />}
        {tab === 'certificates'  && <CertificatesPanel />}
        {tab === 'customers' && <ListView
            title="Bookstore customers"
            testid="bs-admin-customers"
            fetcher={bookstoreAdminApi.listUsers}
            columns={[
              { key: 'email', label: 'Email' },
              { key: 'name',  label: 'Name' },
              { key: 'preferred_genre', label: 'Preferred genre' },
              { key: 'maven_status', label: 'Maven', render: (r) => r.maven_status || '—' },
              { key: 'created_at', label: 'Joined', render: (r) => r.created_at?.slice(0, 10) },
            ]} />}
        {tab === 'orders'    && <ListView
            title="Orders"
            testid="bs-admin-orders"
            fetcher={bookstoreAdminApi.listOrders}
            columns={[
              { key: 'created_at', label: 'When', render: (r) => r.created_at?.slice(0, 16).replace('T', ' ') },
              { key: 'kind',       label: 'Kind' },
              { key: 'bookstore_user_id', label: 'User', render: (r) => r.bookstore_user_id?.slice(0, 14) },
              { key: 'amount_cents', label: 'Amount', render: (r) => `$${((r.amount_cents || 0) / 100).toFixed(2)}` },
              { key: 'payment_status', label: 'Status' },
            ]} />}
        {tab === 'pod'       && <PodQueuePanel />}
        {tab === 'thumbnails' && <MarketingThumbnailEditor />}
        {tab === 'upcoming'  && <UpcomingPanel />}
        {tab === 'waitlist'  && <ListView
            title="Waitlist"
            testid="bs-admin-waitlist"
            fetcher={bookstoreAdminApi.listWaitlist}
            columns={[
              { key: 'email',      label: 'Email' },
              { key: 'source',     label: 'Source' },
              { key: 'created_at', label: 'When', render: (r) => r.created_at?.slice(0, 16).replace('T', ' ') },
            ]} />}
      </main>
    </div>
  );
}
